/**
 * Children's Records Tab
 * Tracks outstanding enrollment documents for each child
 */
import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  FileText, Download, Printer, Mail, Search, 
  ChevronDown, ChevronUp, ClipboardList, Calendar,
  Building2, User, Phone, Save, AlertTriangle, Users, CheckSquare,
  Info, Eye, X, Bell, Settings, Upload, Paperclip, Trash2, BookOpen, Link, CheckCircle, MessageSquare, FileStack
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import PendingEnrollmentsPanel from "@/components/children-records/PendingEnrollmentsPanel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import RecordRemindersSettings from "./RecordRemindersSettings";
import ChildServiceReportDialog from "./ChildServiceReportDialog";
import MultiPageDocumentUploader from "./MultiPageDocumentUploader";
import PdfFieldGuideModal, { PDF_FIELD_CATALOG } from "@/components/enrollment/PdfFieldGuideModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to format date as MM/DD/YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return null;
  // Parse YYYY-MM-DD format and return MM/DD/YYYY
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    const [year, month, day] = parts;
    return `${month}/${day}/${year}`;
  }
  return dateStr;
};

// Helper function to format phone number as (xxx) xxx-xxxx
const formatPhoneNumber = (value) => {
  if (!value) return '';
  
  // Remove all non-digit characters
  const phoneNumber = value.replace(/\D/g, '');
  
  // Get the length of the cleaned number
  const phoneNumberLength = phoneNumber.length;
  
  // Format based on length
  if (phoneNumberLength < 4) {
    return phoneNumber;
  }
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

// Required forms list with renewal periods
// renewalMonths: number of months until return by date from last submitted
// renewalMonths: 0 means "On File" — just needs to exist, no expiration
const REQUIRED_FORMS = [
  { id: 'enrollment_info', name: 'Enrollment Information Sheet', renewalMonths: 0, renewalLabel: 'Optional — No Expiration', isOptional: true },
  { id: 'financial_contract', name: 'Financial & Contract Agreement', renewalMonths: 6, renewalLabel: 'Every 6 Months' },
  { id: 'about_child', name: 'All About Your Child Form', renewalMonths: 0, renewalLabel: 'Optional — No Expiration', isOptional: true },
  { id: 'health_report', name: 'Child Health Report', renewalMonths: 12, renewalLabel: 'Yearly' },
  { id: 'shot_records', name: 'Child Shot Records', renewalMonths: 12, renewalLabel: 'Yearly' },
  { id: 'emergency_consent', name: 'Emergency Contact/Parental Consent Form', renewalMonths: 6, renewalLabel: 'Every 6 Months' },
  { id: 'transportation_policy', name: 'School Age Transportation & Transfer of Care Policy', renewalMonths: 12, renewalLabel: 'Yearly' }
];

// For school-age / after-school children, health_report and shot_records are "On File" only — no annual renewal
const ON_FILE_FORMS_FOR_SCHOOL_AGE = ['health_report', 'shot_records'];

function getFormsForChild(childCategory) {
  const cat = (childCategory || '').toLowerCase();
  const isSchoolAge = cat.includes('school') || cat === 'after_school';
  const isPartTime = cat === 'part_time';

  return REQUIRED_FORMS.map(f => {
    const isHealthShot = ['health_report', 'shot_records'].includes(f.id);
    if (isSchoolAge && isHealthShot) {
      return { ...f, renewalMonths: 0, renewalLabel: 'On File', isSchoolAgeOnFile: true };
    }
    // Transportation policy is only required for school-age children
    if (!isSchoolAge && f.id === 'transportation_policy') {
      return { ...f, isNotRequired: true };
    }
    return f;
  });
}

// Current category is authoritative for Transportation Policy — a school-age child's
// stale notApplicable/N/A must never override the live requirement, even before any
// save is made. Renders Outstanding/Current/Expired purely off the real date.
function getEffectiveFormRecord(formId, childCategory, rawRecord) {
  const cat = (childCategory || '').toLowerCase();
  const isSchoolAge = cat.includes('school') || cat === 'after_school';
  if (formId === 'transportation_policy' && isSchoolAge) {
    const hasStaleFlag = rawRecord?.notApplicable || rawRecord?.lastSubmitted === 'N/A';
    if (hasStaleFlag) {
      const hasValidDate = rawRecord?.lastSubmitted && rawRecord.lastSubmitted !== 'N/A';
      return {
        ...rawRecord,
        notApplicable: false,
        lastSubmitted: hasValidDate ? rawRecord.lastSubmitted : null,
        expirationDate: hasValidDate ? rawRecord.expirationDate : null
      };
    }
  }
  return rawRecord;
}

const ChildrenRecordsTab = ({ organizationName, refreshSignal = 0 }) => {
  const { user } = useAuth();
  const [enrollmentPermitted, setEnrollmentPermitted] = useState(true); // default true to avoid flash
  const [children, setChildren] = useState([]);
  const [childRecords, setChildRecords] = useState({});
  const [expandedChild, setExpandedChild] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bulkEmailSending, setBulkEmailSending] = useState(false);
  
  // Bulk upload state for Date on Form
  const [showBulkDateUpload, setShowBulkDateUpload] = useState(false);
  const [bulkDateUploading, setBulkDateUploading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  
  // Center contact info for the form - centerName will be auto-populated from organization
  const [centerInfo, setCenterInfo] = useState({
    centerName: organizationName || '',
    contactName: '',
    contactTitle: '',
    contactPhone: ''
  });
  
  // Selected children for bulk actions
  const [selectedChildren, setSelectedChildren] = useState(new Set());
  
  // Global return by date for "Apply to All" feature
  const [globalReturnByDate, setGlobalReturnByDate] = useState('');
  
  // Email preview dialog state
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailPreviewData, setEmailPreviewData] = useState([]);
  
  // Individual email confirmation state
  const [showIndividualEmailConfirm, setShowIndividualEmailConfirm] = useState(false);
  const [individualEmailData, setIndividualEmailData] = useState(null);
  const [individualEmailNote, setIndividualEmailNote] = useState("");

  // Individual SMS confirmation state
  const [showIndividualSmsConfirm, setShowIndividualSmsConfirm] = useState(false);
  const [individualSmsData, setIndividualSmsData] = useState(null);
  const [individualSmsNote, setIndividualSmsNote] = useState("");
  const [sendingSms, setSendingSms] = useState(false);
  const smsSendLockRef = useRef(false); // synchronous guard — blocks a second click before React state commits
  
  // Document notifications state
  const [docNotifications, setDocNotifications] = useState([]);
  
  // Form document upload state
  const [uploadingForm, setUploadingForm] = useState(null); // { childId, formId }
  const [pageUploadTarget, setPageUploadTarget] = useState(null); // { childId, formId, formName, existingDoc }
  const [formDocuments, setFormDocuments] = useState({}); // { childId: { formId: documentData } }
  const [docCounts, setDocCounts] = useState({}); // { childId: uploadedFormCount } — at-a-glance
  const [sendingFormLink, setSendingFormLink] = useState(null); // { childId, formId }
  
  // Form upload link email confirmation state
  const [showFormLinkConfirm, setShowFormLinkConfirm] = useState(false);
  const [formLinkEmailData, setFormLinkEmailData] = useState(null); // { childId, formId, childName, formName, parentEmail }

  // Email uploaded files to parent — per-child selected formIds + send state
  const [emailFormSelection, setEmailFormSelection] = useState({}); // { childId: Set<formId> }
  const [showEmailFilesConfirm, setShowEmailFilesConfirm] = useState(false);
  const [emailFilesData, setEmailFilesData] = useState(null); // { childId, childName, parentEmail, formIds, formNames }
  const [emailFilesNote, setEmailFilesNote] = useState("");
  const [sendingEmailFiles, setSendingEmailFiles] = useState(false);

  // Child Service Report dialog state
  const [serviceReportDialogOpen, setServiceReportDialogOpen] = useState(false);
  const [serviceReportChild, setServiceReportChild] = useState(null); // { id, name, birthdate }
  const [serviceReportLookup, setServiceReportLookup] = useState({}); // childId -> latest report

  // Enrollment Packet — template state
  const [enrollmentTemplates, setEnrollmentTemplates] = useState([]);
  const [pdfGuideOpen, setPdfGuideOpen] = useState(false);
  const [pdfGuideInitialTab, setPdfGuideInitialTab] = useState("start");
  const [dryRunResult, setDryRunResult] = useState(null);
  // Deep-link support: ?tab=enrollment auto-opens the Enrollment sub-tab
  const initialTopTab = React.useMemo(() => {
    if (typeof window === "undefined") return "records";
    const params = new URLSearchParams(window.location.search);
    return params.get("tab") === "enrollment" ? "enrollment" : "records";
  }, []);
  // "New" pulse — shows for 30 days after deploy (2026-02-15)
  const isGuideNew = React.useMemo(() => {
    const DEPLOY = new Date("2026-02-15T00:00:00Z").getTime();
    return (Date.now() - DEPLOY) < 30 * 24 * 3600 * 1000;
  }, []);
  const [uploadingTemplate, setUploadingTemplate] = useState(null); // doc type string while uploading
  const [packetStatuses, setPacketStatuses] = useState({});
  const [sendingPacket, setSendingPacket] = useState(null);
  const [packetSelectedChildren, setPacketSelectedChildren] = useState(new Set());
  const [bulkSendProgress, setBulkSendProgress] = useState(null);
  const [bulkPacketNote, setBulkPacketNote] = useState('');
  const [bulkSendViaEmail, setBulkSendViaEmail] = useState(true);
  const [bulkSendViaSMS, setBulkSendViaSMS] = useState(true);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const bulkSendLockRef = useRef(false); // synchronous guard — React state alone can't stop two rapid clicks
  const [packetCategoryFilter, setPacketCategoryFilter] = useState('All');
  const [selectedPacketForms, setSelectedPacketForms] = useState(new Set([
    'shot_records','enrollment_info','financial_contract',
    'about_child','health_report','emergency_consent','transportation_policy'
  ]));
  const togglePacketForm = (key) => {
    setSelectedPacketForms(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };
  const [expandedPacketChild, setExpandedPacketChild] = useState(null);
  const [resendConfirmChild, setResendConfirmChild] = useState(null); // child obj when resend modal open
  const [reviewContacts, setReviewContacts] = useState({}); // { childId: [{name, phone, email, relationship}] }
  const [trackerOpen, setTrackerOpen] = useState(false);
  const [sixMonthInterval, setSixMonthInterval] = useState(5);
  const [sixMonthEnabled, setSixMonthEnabled] = useState(true);
  const [sixMonthNotifications, setSixMonthNotifications] = useState([]);
  const [editingReviewDate, setEditingReviewDate] = useState(null);
  const [selectedReviews, setSelectedReviews] = useState(new Set());

  // Financial Contract Renewal Tracker state
  const [financialTrackerOpen, setFinancialTrackerOpen] = useState(false);
  const [financialInterval, setFinancialInterval] = useState(6);
  const [financialEnabled, setFinancialEnabled] = useState(true);
  const [financialNotifications, setFinancialNotifications] = useState([]);
  const [editingFinancialDate, setEditingFinancialDate] = useState(null);
  const [selectedFinancialReviews, setSelectedFinancialReviews] = useState(new Set());
  const [sendViaEmail, setSendViaEmail] = useState(true);
  const [sendViaSMS, setsendViaSMS] = useState(true);
  const [packetNote, setPacketNote] = useState('');
  const [staffUploading, setStaffUploading] = useState({});
  const [addingCustomForm, setAddingCustomForm] = useState(false);
  const [newCustomLabel, setNewCustomLabel] = useState('');
  const [newCustomReturnRequired, setNewCustomReturnRequired] = useState(true);
  const [uploadingCustom, setUploadingCustom] = useState(false);
  const [selectedCustomFile, setSelectedCustomFile] = useState(null);

  // New Documents Uploaded outer panel collapse state
  const [isDocsPanelOpen, setIsDocsPanelOpen] = useState(false);
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false);
  // Independent open state for notification child folders — keeps them open when View is clicked
  const [expandedNotifChildren, setExpandedNotifChildren] = useState(new Set());

  // Compliance print — as-of date picker
  const todayIso = new Date().toISOString().split('T')[0];
  const [showPrintDatePicker, setShowPrintDatePicker] = useState(false);
  const [printAsOfDate, setPrintAsOfDate] = useState(todayIso);
  const [pendingPrintTarget, setPendingPrintTarget] = useState(null);

  // Tracker print — as-of date picker
  const [showTrackerDatePicker, setShowTrackerDatePicker] = useState(false);
  const [trackerPrintAsOfDate, setTrackerPrintAsOfDate] = useState(todayIso);
  const [pendingTrackerReviews, setPendingTrackerReviews] = useState([]);

  // Check if a child is full-time (not school-age) - eligible for Child Service Report
  const isFullTimeChild = (child) => {
    const category = (child.category || '').toLowerCase();
    const subcategory = (child.fullTimeSubcategory || '').toLowerCase();
    // Exclude school-age children - only show for full-time
    if (category.includes('school') || subcategory.includes('school')) return false;
    return true;
  };

  // Open Child Service Report dialog for a specific child
  const openServiceReportDialog = (child) => {
    setServiceReportChild({
      id: child.id,
      name: child.name || `${child.firstName} ${child.lastName}`.trim(),
      birthdate: child.birthdate,
      pickupPersons: child.pickupPersons
    });
    setServiceReportDialogOpen(true);
  };

  // Check if a form is overdue based on return by date
  const isFormOverdue = (formRecord) => {
    if (!formRecord?.returnBy) return false;
    return new Date(formRecord.returnBy) < new Date();
  };

  useEffect(() => {
    fetchChildren();
    fetchCenterInfo();
    fetchDocNotifications();
    fetchServiceReportStatuses();
    fetchEnrollmentTemplates();
    fetchPacketStatuses();
    fetchSixMonthSettings();
    fetchSixMonthNotifications();
    fetchFinancialReviewSettings();
    fetchFinancialNotifications();
    // Trigger 6-month review check in the background
    axios.post(`${API}/enrollment-packet/run-review-check`, { appUrl: window.location.origin })
      .catch(() => {}); // silent — background task
    // Check enrollment_packet permission
    axios.get(`${API}/owner/permissions`)
      .then(res => {
        const perms = res.data.permissions || {};
        const feature = perms['enrollment_packet'] || {};
        const role = user?.role || 'staff';
        const permitted = role === 'owner' ? true : (feature[role] !== false ? (feature[role] ?? false) : false);
        setEnrollmentPermitted(permitted);
      })
      .catch(() => setEnrollmentPermitted(user?.role === 'owner' || user?.role === 'co-owner'));
  }, []);

  // Refetch children when the parent signals a change (e.g. after Accept Child).
  // Skips the initial render so it doesn't duplicate the mount fetch above.
  const didMountRefresh = useRef(false);
  useEffect(() => {
    if (!didMountRefresh.current) {
      didMountRefresh.current = true;
      return;
    }
    fetchChildren();
  }, [refreshSignal]);

  const fetchDocNotifications = async () => {
    try {
      const response = await axios.get(`${API}/document-notifications`);
      setDocNotifications(response.data.notifications || []);
    } catch (error) {
      console.log('No document notifications');
    }
  };

  // Enrollment Packet — template handlers
  const fetchEnrollmentTemplates = async () => {
    try {
      const res = await axios.get(`${API}/enrollment-packet/templates`);
      setEnrollmentTemplates(res.data.templates || []);
    } catch (e) {
      console.log('Could not fetch enrollment templates');
    }
  };

  const viewEnrollmentTemplate = async (documentType, customId = null) => {
    try {
      const url = customId
        ? `${API}/enrollment-packet/custom-templates/${customId}/view`
        : `${API}/enrollment-packet/templates/${documentType}/view`;
      const res = await axios.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const w = window.open(blobUrl, '_blank');
      if (w) setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (e) {
      toast.error('Could not open template. Please try again.');
    }
  };

  const printFullPacket = async () => {
    try {
      const documentTypes = enrollmentTemplates
        .filter(t => !t.isCustom && selectedPacketForms.has(t.documentType))
        .map(t => t.documentType);
      const res = await axios.post(`${API}/enrollment-packet/print-full-packet`,
        { documentTypes }, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const w = window.open(blobUrl, '_blank');
      if (w) setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (e) {
      toast.error(e.response?.status === 404 ? 'No uploaded templates found to print' : 'Could not build packet');
    }
  };

  const handleTemplateUpload = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingTemplate(docType);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', docType);
      await axios.post(`${API}/enrollment-packet/templates/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { document_type: docType },
      });
      await fetchEnrollmentTemplates();
      toast.success('Template uploaded successfully');

      // Stage 2 — Dry-run field validation
      if (PDF_FIELD_CATALOG[docType]) {
        try {
          const dryForm = new FormData();
          dryForm.append('file', file);
          dryForm.append('document_type', docType);
          const dryRes = await axios.post(`${API}/enrollment-packet/dry-run-mapping`, dryForm, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          setDryRunResult({ ...dryRes.data, formLabel: PDF_FIELD_CATALOG[docType].label });
        } catch (dryErr) {
          // Silent — upload already succeeded. Log for observability.
          console.warn('Dry-run mapping failed', dryErr);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingTemplate(null);
      e.target.value = '';
    }
  };

  const downloadStarterTemplate = async (docType) => {
    try {
      const res = await axios.get(`${API}/enrollment-packet/starter-template/${docType}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${docType}_starter.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Starter template downloaded');
    } catch (err) {
      toast.error('Could not download starter template');
    }
  };

  const deleteEnrollmentTemplate = async (docType) => {
    try {
      await axios.delete(`${API}/enrollment-packet/templates/${docType}`);
      await fetchEnrollmentTemplates();
      toast.success('Template removed');
    } catch (err) {
      toast.error('Could not remove template');
    }
  };

  const handleCustomFormUpload = async () => {
    if (!newCustomLabel.trim()) { toast.error('Please enter a form name first'); return; }
    if (!selectedCustomFile) { toast.error('Please choose a file first'); return; }
    setUploadingCustom(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedCustomFile);
      await axios.post(`${API}/enrollment-packet/custom-templates/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { label: newCustomLabel.trim(), return_required: newCustomReturnRequired },
      });
      await fetchEnrollmentTemplates();
      setNewCustomLabel('');
      setNewCustomReturnRequired(true);
      setSelectedCustomFile(null);
      toast.success('Custom form added successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingCustom(false);
    }
  };

  const deleteCustomTemplate = async (customId) => {
    try {
      await axios.delete(`${API}/enrollment-packet/custom-templates/${customId}`);
      await fetchEnrollmentTemplates();
      toast.success('Custom form removed');
    } catch (err) {
      toast.error('Could not remove custom form');
    }
  };

  const fetchPacketStatuses = async () => {
    try {
      const res = await axios.get(`${API}/enrollment-packet/statuses`);
      setPacketStatuses(res.data.statuses || {});
    } catch (e) {
      console.log('Could not fetch packet statuses');
    }
  };

  const handleStaffUpload = async (childId, docType, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const key = `${childId}_${docType}`;

    // Health Report / Shot Records: the file alone is never a real "Date on Form" —
    // require the actual date printed on the document before uploading.
    let dateOnForm = null;
    if (docType === 'health_report' || docType === 'shot_records') {
      const dateInput = window.prompt('Enter the actual date on this document (MM/DD/YYYY):', getTodayFormatted());
      if (dateInput === null) { e.target.value = ''; return; }
      const parsed = parseUserDate(dateInput.trim());
      if (!parsed || !/^\d{4}-\d{2}-\d{2}$/.test(parsed) || new Date(parsed) > new Date()) {
        toast.error("Enter a valid Date on Form that is not in the future");
        e.target.value = '';
        return;
      }
      dateOnForm = parsed;
    }

    setStaffUploading(prev => ({ ...prev, [key]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const dateParam = dateOnForm ? `&date_on_form=${encodeURIComponent(dateOnForm)}` : '';
      const res = await axios.post(
        `${API}/enrollment-packet/staff-upload/${childId}?document_type=${docType}${dateParam}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setPacketStatuses(prev => ({
        ...prev,
        [childId]: { 
          ...prev[childId], 
          uploadedDocTypes: res.data.uploadedDocTypes,
          ...(res.data.pendingPickupSync ? { pendingPickupSync: res.data.pendingPickupSync } : {})
        }
      }));
      // Refresh the "on file" completion badge from the backend source of truth
      // immediately, instead of waiting for the next unrelated fetch.
      fetchPacketStatuses();
      // Refresh so uploaded doc appears in the file viewer (eye/download icons)
      // and so lastSubmitted/dateReturned dates update on the compliance card
      fetchFormDocuments(childId);
      try {
        const rec = await axios.get(`${API}/children-records/${childId}`);
        setChildRecords(prev => ({ ...prev, [childId]: rec.data.forms || {} }));
      } catch (_) {}
      toast.success(`${docType.replace(/_/g, ' ')} uploaded successfully`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setStaffUploading(prev => ({ ...prev, [key]: false }));
      e.target.value = '';
    }
  };

  const sendEnrollmentPacket = async (child, viaEmail = true, viaSMS = true, note = '') => {
    setSendingPacket(child.id);
    setPacketStatuses(prev => ({
      ...prev,
      [child.id]: {
        ...prev[child.id],
        status: 'waiting',
        uploadedDocTypes: prev[child.id]?.uploadedDocTypes || [],
        lastSentAt: new Date().toISOString(),
      }
    }));
    try {
      const appUrl = window.location.origin;
      const res = await axios.post(`${API}/enrollment-packet/send/${child.id}`, { appUrl, sendEmail: viaEmail, sendSms: viaSMS, selectedForms: [...selectedPacketForms], note: note || undefined });
      const { emailSent, smsSent } = res.data;
      const parts = [];
      if (viaEmail) parts.push(emailSent ? 'Email sent' : 'Email failed');
      if (viaSMS) parts.push(smsSent ? 'SMS sent' : 'SMS failed');
      toast.success(`${parts.join('; ')} for ${child.name}'s parent`);
      fetchPacketStatuses();
    } catch (err) {
      await fetchPacketStatuses();
      toast.error(err.response?.data?.detail || 'Failed to send enrollment packet');
    } finally {
      setSendingPacket(null);
    }
  };

  // ── Stage 4 helpers ─────────────────────────────────────────────────────
  const fetchSixMonthSettings = async () => {
    try {
      const res = await axios.get(`${API}/enrollment-packet/six-month-settings`);
      setSixMonthInterval(res.data.interval || 5);
      setSixMonthEnabled(res.data.enabled !== false);
    } catch {}
  };

  const fetchSixMonthNotifications = async () => {
    try {
      const res = await axios.get(`${API}/enrollment-packet/six-month-notifications`);
      setSixMonthNotifications(res.data.reviews || []);
    } catch {}
  };

  const fetchFinancialReviewSettings = async () => {
    try {
      const res = await axios.get(`${API}/enrollment-packet/financial-review-settings`);
      setFinancialInterval(res.data.interval || 6);
      setFinancialEnabled(res.data.enabled !== false);
    } catch {}
  };

  const fetchFinancialNotifications = async () => {
    try {
      const res = await axios.get(`${API}/enrollment-packet/financial-review-notifications`);
      setFinancialNotifications(res.data.reviews || []);
    } catch {}
  };

  const saveFinancialTrackerSettings = async ({ interval: newInterval, enabled: newEnabled } = {}) => {
    const interval = Math.max(1, Math.min(24, Number(newInterval !== undefined ? newInterval : financialInterval)));
    const enabled = newEnabled !== undefined ? newEnabled : financialEnabled;
    setFinancialInterval(interval);
    setFinancialEnabled(enabled);
    try {
      await axios.post(`${API}/enrollment-packet/financial-review-settings`, { interval, enabled });
      toast.success(enabled ? 'Financial tracker settings saved' : 'Financial tracker disabled');
    } catch { toast.error('Failed to save financial tracker settings'); }
  };

  const handleArchiveFinancialReview = async (id) => {
    try {
      await axios.post(`${API}/enrollment-packet/six-month-notifications/${id}/archive`);
      setFinancialNotifications(prev => prev.filter(r => r.id !== id));
    } catch { toast.error('Failed to archive'); }
  };

  const handleDeleteFinancialReview = async (id) => {
    try {
      await axios.delete(`${API}/enrollment-packet/six-month-notifications/${id}`);
      setFinancialNotifications(prev => prev.filter(r => r.id !== id));
    } catch { toast.error('Failed to delete'); }
  };

  const handleSaveFinancialReviewDate = async (id, value) => {
    try {
      await axios.patch(`${API}/enrollment-packet/six-month-notifications/${id}`, { resubmittedAt: value });
      setFinancialNotifications(prev => prev.map(r => r.id === id ? { ...r, resubmittedAt: value } : r));
      setEditingFinancialDate(null);
    } catch { toast.error('Failed to save date'); }
  };

  const handlePrintFinancialReview = async (review) => {
    try {
      const res = await axios.get(`${API}/enrollment-packet/six-month-notifications/${review.id}/document`);
      const docData = res.data.documentData;
      if (!docData) { toast.error('No document on file for this entry'); return; }
      const byteChars = atob(docData);
      const byteNums = new Array(byteChars.length).fill(0).map((_, i) => byteChars.charCodeAt(i));
      const blob = new Blob([new Uint8Array(byteNums)], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (w) w.onload = () => { w.print(); URL.revokeObjectURL(url); };
    } catch { toast.error('Failed to load document'); }
  };

  const saveTrackerSettings = async ({ interval: newInterval, enabled: newEnabled } = {}) => {
    const interval = Math.max(1, Math.min(24, Number(newInterval !== undefined ? newInterval : sixMonthInterval)));
    const enabled = newEnabled !== undefined ? newEnabled : sixMonthEnabled;
    setSixMonthInterval(interval);
    setSixMonthEnabled(enabled);
    try {
      await axios.post(`${API}/enrollment-packet/six-month-settings`, { interval, enabled });
      toast.success(enabled ? 'Tracker settings saved' : 'Tracker disabled');
    } catch { toast.error('Failed to save tracker settings'); }
  };

  const printTrackerReviews = (reviews, asOfDate) => {
    const displayDate = asOfDate
      ? new Date(asOfDate + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const centerName = centerInfo.centerName || 'ToDays Kidz';
    const rows = reviews.map((r, i) => `
      <tr class="${i % 2 === 0 ? 'row-even' : 'row-odd'}">
        <td>${r.childName}</td>
        <td>Emergency Contact / Parental Consent Form</td>
        <td>${r.resubmittedAt ? new Date(r.resubmittedAt + 'T00:00:00').toLocaleDateString() : '—'}</td>
        <td class="filed-col">&#9634;</td>
      </tr>`).join('');
    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>EC Form Resubmissions — ${centerName}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    @page { size: letter portrait; margin: 0.75in; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #1a1a1a; }
    .header { border-bottom: 2px solid #0d9488; padding-bottom: 10px; margin-bottom: 16px; display: flex; justify-content: space-between; align-items: flex-end; }
    .title { font-size: 17px; font-weight: bold; color: #0d9488; }
    .subtitle { font-size: 11px; color: #555; margin-top: 2px; }
    .meta { text-align: right; font-size: 10px; color: #666; line-height: 1.7; }
    table { width: 100%; border-collapse: collapse; margin-top: 4px; }
    th { background: #0d9488; color: #fff; padding: 7px 10px; text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; }
    td { padding: 7px 10px; font-size: 10.5px; border-bottom: 1px solid #e5e7eb; }
    .row-even { background: #fff; }
    .row-odd  { background: #f0fdfa; }
    .filed-col { text-align: center; font-size: 16px; color: #9ca3af; width: 60px; }
    .footer { margin-top: 20px; border-top: 1px solid #e5e7eb; padding-top: 10px; text-align: center; font-size: 9px; color: #9ca3af; }
    @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="title">${centerName}</div>
      <div class="subtitle">Emergency Contact Form Resubmissions</div>
    </div>
    <div class="meta">Printed: ${displayDate}<br/>Records: ${reviews.length}<br/>For State Compliance Filing</div>
  </div>
  <table>
    <thead>
      <tr>
        <th>Child Name</th>
        <th>Form</th>
        <th>Resubmitted Date</th>
        <th style="text-align:center">Filed</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="footer">Emergency Contact Renewal Tracker &mdash; ${centerName} &mdash; ${displayDate}</div>
</body>
</html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    w.onload = () => w.print();
  };

  const openTrackerPrint = (reviews) => {
    if (!reviews.length) return;
    setPendingTrackerReviews(reviews);
    setTrackerPrintAsOfDate(new Date().toISOString().split('T')[0]);
    setShowTrackerDatePicker(true);
  };

  const handleArchiveReview = async (id) => {    try {
      await axios.post(`${API}/enrollment-packet/six-month-notifications/${id}/archive`);
      setSixMonthNotifications(prev => prev.filter(r => r.id !== id));
      toast.success('Archived');
    } catch { toast.error('Failed to archive'); }
  };

  const handleDeleteReview = async (id) => {
    try {
      await axios.delete(`${API}/enrollment-packet/six-month-notifications/${id}`);
      setSixMonthNotifications(prev => prev.filter(r => r.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleSaveReviewDate = async (id, value) => {
    try {
      await axios.patch(`${API}/enrollment-packet/six-month-notifications/${id}`, { resubmittedAt: value });
      setSixMonthNotifications(prev => prev.map(r => r.id === id ? { ...r, resubmittedAt: value } : r));
      setEditingReviewDate(null);
      toast.success('Date updated');
    } catch { toast.error('Failed to update date'); }
  };

  const handlePrintReview = async (review) => {
    try {
      const res = await axios.get(`${API}/enrollment-packet/six-month-notifications/${review.id}/document`);
      const { documentData } = res.data;
      if (!documentData) { toast.error('No document available to print'); return; }
      const byteChars = atob(documentData);
      const byteArr = new Uint8Array(byteChars.length).map((_, i) => byteChars.charCodeAt(i));
      const blob = new Blob([byteArr], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (win) win.print();
    } catch { toast.error('Failed to load document'); }
  };

  const handleApprovePickupSync = async (childId) => {
    const pending = packetStatuses[childId]?.pendingPickupSync;
    const contacts = reviewContacts[childId] || pending?.contacts || [];
    try {
      await axios.post(`${API}/enrollment-packet/${childId}/approve-pickup-sync`, { contacts });
      setPacketStatuses(prev => {
        const updated = { ...prev[childId] };
        delete updated.pendingPickupSync;
        return { ...prev, [childId]: updated };
      });
      setReviewContacts(prev => { const n = { ...prev }; delete n[childId]; return n; });
      toast.success('Contacts approved and saved to child record');
      fetchChildren();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save contacts');
    }
  };

  const handleDismissPickupSync = async (childId) => {
    try {
      await axios.post(`${API}/enrollment-packet/${childId}/dismiss-pickup-sync`);
      setPacketStatuses(prev => {
        const updated = { ...prev[childId] };
        delete updated.pendingPickupSync;
        return { ...prev, [childId]: updated };
      });
      setReviewContacts(prev => { const n = { ...prev }; delete n[childId]; return n; });
      toast.success('Review dismissed');
    } catch (err) {
      toast.error('Failed to dismiss review');
    }
  };

  const togglePacketChildSelection = (childId) => {
    setPacketSelectedChildren(prev => {
      const next = new Set(prev);
      next.has(childId) ? next.delete(childId) : next.add(childId);
      return next;
    });
  };

  const selectAllNotSent = () => {
    const notSentIds = children
      .filter(c => !packetStatuses[c.id] &&
        (packetCategoryFilter === 'All' ||
         (c.category || '').toLowerCase() === packetCategoryFilter.toLowerCase()))
      .map(c => c.id);
    const allAlreadySelected = notSentIds.every(id => packetSelectedChildren.has(id));
    if (allAlreadySelected) {
      setPacketSelectedChildren(new Set());
    } else {
      setPacketSelectedChildren(new Set(notSentIds));
    }
  };

  const selectAllChildren = () => {
    const allIds = children
      .filter(c => packetCategoryFilter === 'All' ||
        (c.category || '').toLowerCase() === packetCategoryFilter.toLowerCase())
      .map(c => c.id);
    const allAlreadySelected = allIds.length > 0 && allIds.every(id => packetSelectedChildren.has(id));
    if (allAlreadySelected) {
      setPacketSelectedChildren(new Set());
    } else {
      setPacketSelectedChildren(new Set(allIds));
    }
  };

  const sendToSelected = async () => {
    if (bulkSendLockRef.current) return; // synchronous lock — blocks a second confirm click before React state commits
    bulkSendLockRef.current = true;
    const toSend = children.filter(c => packetSelectedChildren.has(c.id));
    if (toSend.length === 0) { bulkSendLockRef.current = false; return; }
    setBulkSendProgress({ current: 0, total: toSend.length });
    let successCount = 0;
    for (let i = 0; i < toSend.length; i++) {
      setBulkSendProgress({ current: i + 1, total: toSend.length });
      try {
        const appUrl = window.location.origin;
        const res = await axios.post(`${API}/enrollment-packet/send/${toSend[i].id}`, { appUrl, sendEmail: bulkSendViaEmail, sendSms: bulkSendViaSMS, selectedForms: [...selectedPacketForms], note: bulkPacketNote || undefined });
        successCount++;
        const { emailSent, smsSent } = res.data;
        if (emailSent !== smsSent) {
          toast.info(`${toSend[i].name}: ${emailSent ? 'Email sent, SMS failed' : 'SMS sent, Email failed'}`);
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed to send to ${toSend[i].name}`);
      }
    }
    await fetchPacketStatuses();
    setPacketSelectedChildren(new Set());
    setBulkPacketNote('');
    setBulkSendViaEmail(true);
    setBulkSendViaSMS(true);
    setBulkSendProgress(null);
    bulkSendLockRef.current = false;
    toast.success(`Enrollment packets sent to ${successCount} of ${toSend.length} families`);
  };

  const clearNotification = async (notificationId) => {
    try {
      await axios.post(`${API}/document-notifications/${notificationId}/mark-reviewed`);
      setDocNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Notification cleared');
    } catch (error) {
      toast.error('Failed to clear notification');
    }
  };

  const clearAllNotifications = async () => {
    try {
      await axios.post(`${API}/document-notifications/clear-all`);
      setDocNotifications([]);
      toast.success('All notifications cleared');
    } catch (error) {
      toast.error('Failed to clear notifications');
    }
  };

  const fetchServiceReportStatuses = async () => {
    try {
      const response = await axios.get(`${API}/child-service-reports`);
      const reports = response.data || [];
      const lookup = {};
      for (const report of reports) {
        const cid = report.childId;
        if (!lookup[cid] || new Date(report.savedAt || report.createdAt) > new Date(lookup[cid].savedAt || lookup[cid].createdAt)) {
          lookup[cid] = report;
        }
      }
      setServiceReportLookup(lookup);
    } catch (error) {
      console.log('Could not fetch service report statuses');
    }
  };

  // Helper function to parse name field into firstName and lastName
  const parseName = (child) => {
    if (child.firstName && child.lastName) {
      return { firstName: child.firstName, lastName: child.lastName };
    }
    // Handle "LastName, FirstName" format
    if (child.name && child.name.includes(',')) {
      const parts = child.name.split(',').map(p => p.trim());
      return { lastName: parts[0] || '', firstName: parts[1] || '' };
    }
    // Fallback: treat entire name as firstName
    return { firstName: child.name || '', lastName: '' };
  };

  const fetchChildren = async () => {
    try {
      const response = await axios.get(`${API}/children`);
      const activeChildren = (response.data || []).filter(c => c.active !== false).map(child => ({
        ...child,
        ...parseName(child)
      }));
      setChildren(activeChildren);
      
      // Fetch records for each child
      const recordsResponse = await axios.get(`${API}/children-records`);
      const recordsMap = {};
      (recordsResponse.data || []).forEach(record => {
        recordsMap[record.childId] = record.forms || {};
      });
      setChildRecords(recordsMap);

      // Uploaded-doc counts for the at-a-glance paperclip badge (non-blocking)
      try {
        const countsRes = await axios.get(`${API}/children-records/form-document-counts`);
        setDocCounts(countsRes.data || {});
      } catch (_) {}
    } catch (error) {
      console.error('Error fetching children:', error);
      toast.error('Failed to load children');
    } finally {
      setLoading(false);
    }
  };

  const fetchCenterInfo = async () => {
    try {
      // Try to get organization info for center name
      const orgResponse = await axios.get(`${API}/auth/me`);
      const orgName = orgResponse.data?.organization?.name || organizationName || '';
      
      // Try to get saved contact info
      const response = await axios.get(`${API}/organization/records-contact`);
      setCenterInfo({
        centerName: orgName, // Always use organization name
        contactName: response.data?.contactName || '',
        contactTitle: response.data?.contactTitle || '',
        contactPhone: response.data?.contactPhone || ''
      });
    } catch (error) {
      // If endpoint fails, just use the organization name passed in
      setCenterInfo(prev => ({
        ...prev,
        centerName: organizationName || prev.centerName
      }));
    }
  };

  const saveCenterInfo = async () => {
    try {
      await axios.put(`${API}/organization/records-contact`, centerInfo);
      toast.success('Center contact info saved');
    } catch (error) {
      console.error('Error saving center info:', error);
      toast.error('Failed to save center info');
    }
  };

  const toggleFormStatus = async (childId, formId, isOutstanding) => {
    const currentRecords = childRecords[childId] || {};
    const updatedForms = {
      ...currentRecords,
      [formId]: {
        ...currentRecords[formId],
        outstanding: isOutstanding,
        updatedAt: new Date().toISOString()
      }
    };
    
    setChildRecords(prev => ({
      ...prev,
      [childId]: updatedForms
    }));

    // Save to backend
    try {
      await axios.put(`${API}/children-records/${childId}`, { forms: updatedForms });
    } catch (error) {
      console.error('Error saving record:', error);
      toast.error('Failed to save record status');
    }
  };

  // Helper to get child category by ID
  const getChildCategory = (childId) => {
    const child = children.find(c => c.id === childId);
    return child?.category || '';
  };

  // Helper function to calculate expiration date based on renewal period
  // For school-age children, health_report and shot_records are "On File" (no expiration)
  // For part-time children, health/shot only get expiry when expiryEnabled toggle is on
  const calculateExpirationDate = (submittedDate, formId, childCategory, expiryEnabled = false) => {
    if (!submittedDate) return null;
    
    const forms = childCategory ? getFormsForChild(childCategory) : REQUIRED_FORMS;
    const form = forms.find(f => f.id === formId);
    if (!form) return null;

    // Part-time optional expiry: only calculate if toggle is on
    if (form.isPartTimeOptionalExpiry && !expiryEnabled) return null;

    if (!form.renewalMonths) return null;
    
    const submitted = new Date(submittedDate);
    if (isNaN(submitted.getTime())) return null;
    
    const expirationDate = new Date(submitted);
    expirationDate.setMonth(expirationDate.getMonth() + form.renewalMonths);
    return expirationDate.toISOString().split('T')[0];
  };

  // Toggle expiry tracking for part-time health/shot records
  const toggleExpiryEnabled = async (childId, formId, enabled) => {
    const currentRecords = childRecords[childId] || {};
    const existing = currentRecords[formId] || {};
    // Recalculate expiry based on new toggle state
    const expiry = enabled
      ? calculateExpirationDate(existing.lastSubmitted, formId, getChildCategory(childId), true)
      : null;
    const updatedForms = {
      ...currentRecords,
      [formId]: { ...existing, expiryEnabled: enabled, expirationDate: expiry, updatedAt: new Date().toISOString() }
    };
    setChildRecords(prev => ({ ...prev, [childId]: updatedForms }));
    try {
      await axios.put(`${API}/children-records/${childId}`, { forms: updatedForms });
      toast.success(enabled ? 'Expiration tracking enabled' : 'Switched to On File (no expiration)');
    } catch { toast.error('Failed to update'); }
  };

  // Update the "Date Last Submitted" field directly (historical record)
  const updateLastSubmittedDate = async (childId, formId, date) => {
    const currentRecords = childRecords[childId] || {};
    
    // Auto-calculate expiration date based on form's renewal period
    const calculatedExpiration = date ? calculateExpirationDate(date, formId, getChildCategory(childId), childRecords[childId]?.[formId]?.expiryEnabled) : null;
    
    const updatedForms = {
      ...currentRecords,
      [formId]: {
        ...currentRecords[formId],
        notApplicable: false, // current category is authoritative — a real edit always overrides a stale N/A
        lastSubmitted: date || null,
        expirationDate: calculatedExpiration, // Auto-set expiration (yearly or 6 month)
        updatedAt: new Date().toISOString()
      }
    };
    
    setChildRecords(prev => ({
      ...prev,
      [childId]: updatedForms
    }));

    try {
      await axios.put(`${API}/children-records/${childId}`, { forms: updatedForms, clearedFormIds: !date ? [formId] : [] });
      if (date && calculatedExpiration) {
        const form = REQUIRED_FORMS.find(f => f.id === formId);
        toast.success(`Last submitted date saved. Expires: ${calculatedExpiration} (${form?.renewalLabel || ''})`);
      } else if (!date) {
        toast.success('Date cleared successfully');
      }
    } catch (error) {
      console.error('Error saving date:', error);
      toast.error('Failed to save date');
    }
  };

  // Saves the Date-on-Form draft for a pending replacement the moment it's typed
  // (onBlur) — so it survives a refresh instead of being lost. Approve reads this
  // same saved value; it stays a separate, explicit action.
  const saveDraftDateOnForm = async (childId, formId, date) => {
    try {
      await axios.patch(`${API}/children-records/${childId}/form-document/${formId}/pending-draft-date`, {
        date_on_form: date || null
      });
      setFormDocuments(prev => ({
        ...prev,
        [childId]: {
          ...prev[childId],
          [formId]: {
            ...prev[childId]?.[formId],
            pendingVersion: { ...prev[childId]?.[formId]?.pendingVersion, draftDateOnForm: date || null }
          }
        }
      }));
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save date');
    }
  };

  // Clear all dates for a specific form (reset to never submitted state)
  const clearFormDates = async (childId, formId) => {
    if (!window.confirm('Clear all dates for this form? This will reset it to "Never Submitted" state.')) {
      return;
    }
    
    const currentRecords = childRecords[childId] || {};
    
    const updatedForms = {
      ...currentRecords,
      [formId]: {
        ...currentRecords[formId],
        lastSubmitted: null,
        expirationDate: null,
        dateReturned: null,
        returnBy: null,
        outstanding: true,
        updatedAt: new Date().toISOString()
      }
    };
    
    setChildRecords(prev => ({
      ...prev,
      [childId]: updatedForms
    }));

    try {
      await axios.put(`${API}/children-records/${childId}`, { forms: updatedForms, clearedFormIds: [formId] });
      toast.success('Form dates cleared successfully');
    } catch (error) {
      console.error('Error clearing dates:', error);
      toast.error('Failed to clear dates');
    }
  };

  // Update Return By date - using onBlur to avoid input interruption
  const updateReturnByDate = async (childId, formId, date) => {
    const currentRecords = childRecords[childId] || {};
    
    // Explicitly handle empty/cleared date - set to null, not empty string
    const dateValue = date && date.trim() !== '' ? date : null;
    
    const updatedForms = {
      ...currentRecords,
      [formId]: {
        ...currentRecords[formId],
        returnBy: dateValue,
        updatedAt: new Date().toISOString()
      }
    };
    
    // Update local state immediately for responsive UI
    setChildRecords(prev => ({
      ...prev,
      [childId]: updatedForms
    }));

    try {
      await axios.put(`${API}/children-records/${childId}`, { forms: updatedForms });
      if (!dateValue) {
        // Force a visual update when clearing the date
        toast.success('Return By date cleared');
      }
    } catch (error) {
      console.error('Error saving return by date:', error);
      toast.error('Failed to save date');
    }
  };

  // Helper function to convert MM/DD/YYYY to YYYY-MM-DD for storage
  const parseUserDate = (dateStr) => {
    if (!dateStr) return null;
    // Check if already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    // Parse MM/DD/YYYY format
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      // Pad month and day with leading zeros if needed
      const paddedMonth = month.padStart(2, '0');
      const paddedDay = day.padStart(2, '0');
      return `${year}-${paddedMonth}-${paddedDay}`;
    }
    return dateStr;
  };

  // Helper function to get today's date in MM/DD/YYYY format
  const getTodayFormatted = () => {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const year = today.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Mark a record as returned - prompts for date and updates all fields
  const markRecordReturned = async (childId, formId) => {
    const todayFormatted = getTodayFormatted();
    const dateInput = window.prompt('Enter the date on the form (MM/DD/YYYY):', todayFormatted);
    
    if (!dateInput) return; // User cancelled
    
    // Convert user input to YYYY-MM-DD for storage
    const dateForStorage = parseUserDate(dateInput);
    
    const currentRecords = childRecords[childId] || {};
    
    // Auto-calculate expiration date based on form's renewal period
    const calculatedExpiration = calculateExpirationDate(dateForStorage, formId, getChildCategory(childId));
    
    const updatedForms = {
      ...currentRecords,
      [formId]: {
        ...currentRecords[formId],
        notApplicable: false,                  // current category is authoritative — a real edit always overrides a stale N/A
        outstanding: false,
        lastSubmitted: dateForStorage,         // Date on form
        expirationDate: calculatedExpiration,  // Auto-calculate expiration
        returnBy: null,                        // Clear return by since form was returned
        updatedAt: new Date().toISOString()
      }
    };
    
    setChildRecords(prev => ({
      ...prev,
      [childId]: updatedForms
    }));

    try {
      await axios.put(`${API}/children-records/${childId}`, { forms: updatedForms });
      const form = REQUIRED_FORMS.find(f => f.id === formId);
      toast.success(`Record marked as returned. Expires: ${formatDate(calculatedExpiration) || 'N/A'} (${form?.renewalLabel || ''})`);
    } catch (error) {
      console.error('Error marking record returned:', error);
      toast.error('Failed to update record');
    }
  };

  // Handle form document upload
  // Prompts for the required "Date on Form" for Health/Shot Records — used by both
  // the legacy single-file path and the new multi-page composer's Save.
  // Returns the parsed date, undefined if not required, or null if cancelled/invalid.
  const promptDateOnFormIfRequired = (formId) => {
    if (formId !== 'health_report' && formId !== 'shot_records') return undefined;
    const dateInput = window.prompt('Enter the actual date on this document (MM/DD/YYYY):', getTodayFormatted());
    if (dateInput === null) return null;
    const parsed = parseUserDate(dateInput.trim());
    if (!parsed || !/^\d{4}-\d{2}-\d{2}$/.test(parsed) || new Date(parsed) > new Date()) {
      toast.error("Enter a valid Date on Form that is not in the future");
      return null;
    }
    return parsed;
  };

  // Core reusable upload call — used by the legacy single-file path, the ordinary
  // single-PDF passthrough, and the new multi-page composer's combined PDF save.
  const uploadFormDocumentFile = async (childId, formId, file, dateOnForm) => {
    setUploadingForm({ childId, formId });
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('formId', formId);
      if (dateOnForm) formData.append('dateOnForm', dateOnForm);

      const response = await axios.post(`${API}/children-records/${childId}/form-document`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormDocuments(prev => ({
        ...prev,
        [childId]: {
          ...(prev[childId] || {}),
          [formId]: response.data.document
        }
      }));

      if (response.data.uploadedDocTypes && response.data.uploadedDocTypes.length > 0) {
        setPacketStatuses(prev => ({
          ...prev,
          [childId]: {
            ...(prev[childId] || {}),
            uploadedDocTypes: response.data.uploadedDocTypes,
            ...(response.data.packetStatus ? { status: response.data.packetStatus } : {}),
            ...(response.data.pendingPickupSync ? { pendingPickupSync: response.data.pendingPickupSync } : {}),
          }
        }));
      }
      fetchPacketStatuses();
      try {
        const rec = await axios.get(`${API}/children-records/${childId}`);
        setChildRecords(prev => ({ ...prev, [childId]: rec.data.forms || {} }));
      } catch (_) {}

      const form = REQUIRED_FORMS.find(f => f.id === formId);
      toast.success(`${form?.name || 'Document'} uploaded successfully`);
      return true;
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload document");
      return false;
    } finally {
      setUploadingForm(null);
    }
  };

  const openPageUploadModal = async (childId, formId, formName, existingDocHint) => {
    if (existingDocHint) {
      // "Add Pages" must always load the real, current document from the server —
      // never trust a possibly-stale local copy of formDocuments. If the server has
      // no document for this form, stop and surface an error instead of silently
      // falling back to the (possibly stale) local hint.
      try {
        const res = await axios.get(`${API}/children-records/${childId}/form-documents`);
        const fresh = res.data?.documents?.[formId];
        if (!fresh) {
          toast.error("The current document could not be found on the server. Please refresh and try again.");
          return;
        }
        setPageUploadTarget({ childId, formId, formName, existingDoc: fresh });
      } catch (error) {
        toast.error("Could not load the existing document. Please try again.");
      }
      return;
    }
    setPageUploadTarget({ childId, formId, formName, existingDoc: null });
  };

  // View uploaded form document
  const viewFormDocument = async (childId, formId, options = {}) => {
    // Open the tab synchronously (within the click's user-gesture) so browsers
    // don't treat it as a blocked popup once we hit the `await` below.
    // Note: no 'noopener' here — that flag makes window.open() return null in
    // modern browsers, which broke navigating this tab after the ticket call.
    const win = window.open('', '_blank');
    if (!win) {
      toast.error('Please allow pop-ups to view this document.');
      return;
    }
    try {
      const variant = options.pending ? 'pending' : 'current';
      const res = await axios.post(`${API}/children-records/${childId}/form-document/${formId}/view-ticket?variant=${variant}`);
      const ticket = res.data?.ticket;
      if (!ticket) throw new Error('No ticket returned');
      win.opener = null; // sever the opener link for security without losing the window reference
      win.location.href = `${API}/children-records/form-documents/view/${ticket}`;
    } catch (err) {
      win.close();
      toast.error('Unable to open document. It may have been removed.');
    }
  };

  // Download uploaded form document
  const downloadFormDocument = (doc, formId, childName) => {
    try {
      if (!doc || !doc.data) {
        toast.error('This document has no file data to download. Try re-uploading it.');
        return;
      }
      const byteCharacters = atob(doc.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: doc.contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const form = REQUIRED_FORMS.find(f => f.id === formId);
      a.download = doc.filename || `${form?.name || formId}_${childName.replace(/[^a-z0-9]/gi, '_')}.${(doc.contentType || 'application/pdf').split('/')[1]}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('Could not download this document. The file data may be corrupted — try re-uploading it.');
    }
  };

  // Delete uploaded form document
  const deleteFormDocument = async (childId, formId) => {
    if (!window.confirm('Delete this uploaded document?')) return;
    
    try {
      await axios.delete(`${API}/children-records/${childId}/form-document/${formId}`);
      
      // Remove from local state
      setFormDocuments(prev => {
        const childDocs = { ...(prev[childId] || {}) };
        delete childDocs[formId];
        return { ...prev, [childId]: childDocs };
      });

      // Refresh compliance dates for this child so dates clear without hard refresh
      try {
        const rec = await axios.get(`${API}/children-records/${childId}`);
        setChildRecords(prev => ({ ...prev, [childId]: rec.data.forms || {} }));
      } catch (_) {}

      // Refresh enrollment packet status badges
      fetchPacketStatuses();

      // Clear matching notifications from the New Documents Uploaded panel
      const matching = docNotifications.filter(
        n => n.childId === childId && n.documentType === formId
      );
      if (matching.length > 0) {
        setDocNotifications(prev =>
          prev.filter(n => !(n.childId === childId && n.documentType === formId))
        );
        matching.forEach(n =>
          axios.post(`${API}/document-notifications/${n.id}/mark-reviewed`).catch(() => {})
        );
      }
      
      toast.success('Document deleted');
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  // Approve a pending replacement -> promotes to current, archives previous current
  const approveReplacement = async (childId, formId, dateOnForm) => {
    if (!window.confirm('Approve this replacement? It becomes the current document and the previous version is archived to history.')) return;
    try {
      await axios.post(`${API}/children-records/${childId}/form-document/${formId}/approve-replacement`, {
        date_on_form: dateOnForm || undefined
      });
      await fetchFormDocuments(childId);
      try {
        const rec = await axios.get(`${API}/children-records/${childId}`);
        setChildRecords(prev => ({ ...prev, [childId]: rec.data.forms || {} }));
      } catch (_) {}
      fetchPacketStatuses();
      toast.success('Replacement approved — now the current document');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to approve replacement');
    }
  };

  // Request Again / Reject -> discards ONLY the pending replacement, keeps current + history
  const rejectReplacement = async (childId, formId) => {
    if (!window.confirm('Discard this pending replacement? The current document stays in place and the parent can upload again.')) return;
    try {
      await axios.post(`${API}/children-records/${childId}/form-document/${formId}/reject-replacement`);
      await fetchFormDocuments(childId);
      fetchPacketStatuses();
      toast.success('Pending replacement discarded — parent can upload again');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to discard replacement');
    }
  };

  // Fetch form documents for a child
  const fetchFormDocuments = async (childId) => {
    try {
      const response = await axios.get(`${API}/children-records/${childId}/form-documents`);
      setFormDocuments(prev => ({
        ...prev,
        [childId]: response.data.documents || {}
      }));
    } catch (error) {
      console.log('No form documents found');
    }
  };

  // Handle expanding a child row - also fetches form documents AND refreshes compliance dates
  const handleExpandChild = async (childId) => {
    if (expandedChild === childId) {
      setExpandedChild(null);
    } else {
      setExpandedChild(childId);
      // Stage 4B: lazily backfill any staged Pending documents (standard + custom)
      // for an already-accepted child, then load docs so they appear immediately.
      try {
        await axios.post(`${API}/pending-enrollment/child/${childId}/ensure-handoff`);
      } catch (e) {
        // no-op: child may not originate from a pending enrollment
      }
      // Always fetch fresh so newly uploaded files appear immediately
      fetchFormDocuments(childId);
      // Also refresh compliance dates so parent uploads via link show immediately without hard refresh
      try {
        const rec = await axios.get(`${API}/children-records/${childId}`);
        setChildRecords(prev => ({ ...prev, [childId]: rec.data.forms || {} }));
      } catch (_) {}
    }
  };

  // Prepare form upload link email - show confirmation first
  const prepareFormLinkEmail = (childId, formId, childName) => {
    const child = children.find(c => c.id === childId);
    if (!child) return;
    
    const form = REQUIRED_FORMS.find(f => f.id === formId);
    if (!form) return;
    
    // Get parent email from pickup persons
    const parentEmail = child.pickupPersons?.find(p => p.email)?.email;
    
    if (!parentEmail) {
      toast.error('No parent email found for this child');
      return;
    }
    
    // Set data for confirmation dialog
    setFormLinkEmailData({
      childId,
      formId,
      childName: child.name || childName,
      formName: form.name,
      parentEmail
    });
    setShowFormLinkConfirm(true);
  };

  // Actually send form upload link after confirmation
  const confirmAndSendFormLink = async () => {
    if (!formLinkEmailData) return;
    
    const { childId, formId } = formLinkEmailData;
    setShowFormLinkConfirm(false);
    setSendingFormLink({ childId, formId });
    
    try {
      const response = await axios.post(`${API}/children-records/${childId}/send-form-upload-links`, {
        formIds: [formId],
        customMessage: null
      });
      
      toast.success(response.data.message || "Upload link sent to parent!");
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to send upload link";
      toast.error(errorMsg);
    } finally {
      setSendingFormLink(null);
      setFormLinkEmailData(null);
    }
  };

  // ==== Email Uploaded Files to Parent (multi-select per child) ====
  const toggleEmailFormSelection = (childId, formId, checked) => {
    setEmailFormSelection(prev => {
      const next = { ...prev };
      const set = new Set(next[childId] || []);
      if (checked) set.add(formId); else set.delete(formId);
      if (set.size === 0) delete next[childId]; else next[childId] = set;
      return next;
    });
  };

  const getParentEmailForChild = (child) => {
    return child?.pickupPersons?.find(p => p.email)?.email || null;
  };

  const prepareEmailFilesToParent = (child) => {
    const selected = Array.from(emailFormSelection[child.id] || []);
    if (selected.length === 0) {
      toast.error("Select at least one form to email.");
      return;
    }
    const parentEmail = getParentEmailForChild(child);
    if (!parentEmail) {
      toast.error("No parent email on file for this child.");
      return;
    }
    const forms = getFormsForChild(child.category || '');
    const formNames = selected.map(fid => forms.find(f => f.id === fid)?.name || fid);
    setEmailFilesData({
      childId: child.id,
      childName: child.name,
      parentEmail,
      formIds: selected,
      formNames,
    });
    setEmailFilesNote("");
    setShowEmailFilesConfirm(true);
  };

  const confirmAndEmailFilesToParent = async () => {
    if (!emailFilesData) return;
    const { childId, formIds, parentEmail } = emailFilesData;
    setSendingEmailFiles(true);
    try {
      const res = await axios.post(`${API}/children-records/${childId}/email-uploaded-files`, {
        formIds,
        customMessage: emailFilesNote || null,
      });
      const list = (res.data.forms || []).join(", ");
      toast.success(`Emailed ${res.data.fileCount} form(s) to ${parentEmail}${list ? `: ${list}` : ""}`);
      // Clear selection for this child on success
      setEmailFormSelection(prev => {
        const next = { ...prev };
        delete next[childId];
        return next;
      });
      setShowEmailFilesConfirm(false);
      setEmailFilesData(null);
      setEmailFilesNote("");
    } catch (error) {
      const msg = error?.response?.data?.detail || "Failed to send email";
      toast.error(msg);
    } finally {
      setSendingEmailFiles(false);
    }
  };

  // Apply global return by date to all outstanding forms for a specific child
  const applyGlobalReturnByToChild = async (childId) => {
    if (!globalReturnByDate) {
      toast.error('Please set a Return By date first');
      return;
    }

    const currentRecords = childRecords[childId] || {};
    const updatedForms = { ...currentRecords };
    
    // Update all outstanding forms with the global return by date
    REQUIRED_FORMS.forEach(form => {
      if (currentRecords[form.id]?.outstanding) {
        updatedForms[form.id] = {
          ...currentRecords[form.id],
          returnBy: globalReturnByDate,
          updatedAt: new Date().toISOString()
        };
      }
    });
    
    setChildRecords(prev => ({
      ...prev,
      [childId]: updatedForms
    }));

    try {
      await axios.put(`${API}/children-records/${childId}`, { forms: updatedForms });
      toast.success('Return By date applied to all outstanding forms');
    } catch (error) {
      console.error('Error applying global date:', error);
      toast.error('Failed to apply date');
    }
  };

  // Apply global return by date to ALL selected children's outstanding forms
  const applyGlobalReturnByToAllSelected = async () => {
    if (!globalReturnByDate) {
      toast.error('Please set a Return By date first');
      return;
    }

    if (selectedChildren.size === 0) {
      toast.error('Please select children first');
      return;
    }

    const childrenToUpdate = filteredChildren.filter(child => 
      selectedChildren.has(child.id) && getOutstandingForms(child.id).length > 0
    );

    if (childrenToUpdate.length === 0) {
      toast.error('No selected children have outstanding forms');
      return;
    }

    if (!window.confirm(`Apply Return By date "${globalReturnByDate}" to all outstanding forms for ${childrenToUpdate.length} child(ren)?`)) {
      return;
    }

    setSaving(true);
    let successCount = 0;

    for (const child of childrenToUpdate) {
      const currentRecords = childRecords[child.id] || {};
      const updatedForms = { ...currentRecords };
      
      REQUIRED_FORMS.forEach(form => {
        if (currentRecords[form.id]?.outstanding) {
          updatedForms[form.id] = {
            ...currentRecords[form.id],
            returnBy: globalReturnByDate,
            updatedAt: new Date().toISOString()
          };
        }
      });
      
      setChildRecords(prev => ({
        ...prev,
        [child.id]: updatedForms
      }));

      try {
        await axios.put(`${API}/children-records/${child.id}`, { forms: updatedForms });
        successCount++;
      } catch (error) {
        console.error(`Error updating ${child.firstName}:`, error);
      }
    }

    setSaving(false);
    toast.success(`Return By date applied to ${successCount} child(ren)`);
  };

  // Apply global return by date to ALL children (not just selected)
  const applyGlobalReturnByToAllChildren = async () => {
    if (!globalReturnByDate) {
      toast.error('Please set a Return By date first');
      return;
    }

    // Get all children with outstanding forms
    const childrenWithOutstanding = filteredChildren.filter(c => getOutstandingForms(c.id).length > 0);

    if (childrenWithOutstanding.length === 0) {
      toast.error('No children have outstanding forms');
      return;
    }

    if (!window.confirm(`Apply Return By date "${globalReturnByDate}" to ALL ${childrenWithOutstanding.length} children with outstanding forms?\n\nThis is typically used at the beginning of the year when everyone needs to turn in records.`)) {
      return;
    }

    setSaving(true);
    let successCount = 0;

    for (const child of childrenWithOutstanding) {
      const currentRecords = childRecords[child.id] || {};
      const updatedForms = { ...currentRecords };
      
      REQUIRED_FORMS.forEach(form => {
        if (currentRecords[form.id]?.outstanding) {
          updatedForms[form.id] = {
            ...currentRecords[form.id],
            returnBy: globalReturnByDate,
            updatedAt: new Date().toISOString()
          };
        }
      });
      
      setChildRecords(prev => ({
        ...prev,
        [child.id]: updatedForms
      }));

      try {
        await axios.put(`${API}/children-records/${child.id}`, { forms: updatedForms });
        successCount++;
      } catch (error) {
        console.error(`Error updating ${child.firstName}:`, error);
      }
    }

    setSaving(false);
    toast.success(`Return By date applied to ALL ${successCount} children with outstanding forms`);
  };

  // Download Excel template for bulk Date on Form upload
  const downloadBulkDateTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const response = await axios.get(`${API}/children-records/bulk-date-template`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Date_on_Form_Template_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded successfully');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // Handle bulk Date on Form upload
  const handleBulkDateUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setBulkDateUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/children-records/bulk-date-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Update local state with the results
      if (response.data.updatedRecords) {
        for (const [childId, forms] of Object.entries(response.data.updatedRecords)) {
          setChildRecords(prev => ({
            ...prev,
            [childId]: { ...prev[childId], ...forms }
          }));
        }
      }

      toast.success(`Successfully updated dates for ${response.data.childrenUpdated} children`);
      setShowBulkDateUpload(false);
    } catch (error) {
      console.error('Error uploading:', error);
      toast.error(error.response?.data?.detail || 'Failed to process upload');
    } finally {
      setBulkDateUploading(false);
      e.target.value = ''; // Reset file input
    }
  };

  const getOutstandingForms = (childId) => {
    const records = childRecords[childId] || {};
    const child = children.find(c => c.id === childId);
    const forms = getFormsForChild(child?.category);
    return forms.filter(form => {
      if (form.isOptional || form.isNotRequired) return false;
      const rec = getEffectiveFormRecord(form.id, child?.category, records[form.id]);
      // No record at all = form never submitted = outstanding
      if (!rec) return true;
      // N/A forms are not outstanding
      if (rec.notApplicable || rec.lastSubmitted === "N/A") return false;
      // Empty date (record exists but no submission date) = outstanding
      if (!rec.lastSubmitted) return true;
      // Check if explicitly marked outstanding
      if (rec.outstanding === true) return true;
      // Also check if the form has expired (expiration date in the past)
      const expDate = rec.expirationDate || calculateExpirationDate(rec.lastSubmitted, form.id, child?.category);
      if (expDate && new Date(expDate + 'T00:00:00') < new Date()) return true;
      return false;
    });
  };

  const generatePDF = async (childId) => {
    const child = children.find(c => c.id === childId);
    if (!child) return;

    const outstandingForms = getOutstandingForms(childId);
    const records = childRecords[childId] || {};
    
    try {
      const response = await axios.post(`${API}/children-records/generate-pdf`, {
        childId,
        childName: `${child.firstName} ${child.lastName}`,
        outstandingForms: outstandingForms.map(f => {
          const formRecord = getEffectiveFormRecord(f.id, child.category, records[f.id] || {});
          return {
            name: f.name,
            renewalLabel: f.renewalLabel,
            lastSubmitted: formRecord.lastSubmitted || null,
            returnBy: formRecord.returnBy || null
          };
        }),
        centerInfo
      }, { responseType: 'blob' });

      // Download the PDF
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${child.lastName}_${child.firstName}_Outstanding_Records.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const printRecord = async (childId) => {
    const child = children.find(c => c.id === childId);
    if (!child) return;

    const outstandingForms = getOutstandingForms(childId);
    const records = childRecords[childId] || {};
    
    // Build forms HTML separately - showing all 4 date fields
    const formsHtml = outstandingForms.map(form => {
      const formRecord = getEffectiveFormRecord(form.id, child.category, records[form.id] || {});
      const returnByDate = formRecord.returnBy;
      const isOverdue = returnByDate && new Date(returnByDate) < new Date();
      const overdueClass = isOverdue ? 'overdue' : '';
      
      // Handle N/A forms
      const isNA = formRecord.notApplicable || formRecord.lastSubmitted === "N/A";
      
      if (isNA) {
        return `
          <div class="form-item">
            <span class="form-name">☐ ${form.name}</span>
            <span class="form-renewal">(${form.renewalLabel})</span>
            <div class="form-dates">
              <span style="color: #6b7280; font-style: italic;">N/A - Not Applicable</span>
            </div>
          </div>
        `;
      }
      
      // Calculate expiration date if not already set
      const expirationDate = formRecord.expirationDate || calculateExpirationDate(formRecord.lastSubmitted, form.id, child.category);
      const isExpired = expirationDate && new Date(expirationDate) < new Date();
      
      // Format all 4 dates for display (MM/DD/YYYY format)
      const lastSubmittedText = `Last Submitted: ${formRecord.lastSubmitted ? formatDate(formRecord.lastSubmitted) : 'Never'}`;
      const expirationText = ` | <span class="${isExpired ? 'overdue' : ''}">Expires: ${formatDate(expirationDate) || 'N/A'}${isExpired ? ' (EXPIRED)' : ''}</span>`;
      const returnByText = returnByDate ? ` | <span class="${overdueClass}">Return By: ${formatDate(returnByDate)}${isOverdue ? ' (OVERDUE)' : ''}</span>` : '';
      
      return `
        <div class="form-item">
          <span class="form-name">☐ ${form.name}</span>
          <span class="form-renewal">(${form.renewalLabel})</span>
          <div class="form-dates">
            ${lastSubmittedText}${expirationText}${returnByText}
          </div>
        </div>
      `;
    }).join('');
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Outstanding Records - ${child.firstName} ${child.lastName}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          h1 { color: #1e3a5f; border-bottom: 2px solid #1e3a5f; padding-bottom: 10px; }
          .header { text-align: center; margin-bottom: 30px; }
          .child-info { background: #f5f5f5; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .warning { background: #fff3cd; border: 1px solid #ffc107; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .forms-list { margin: 20px 0; }
          .form-item { padding: 10px; border-bottom: 1px solid #ddd; }
          .form-name { font-weight: bold; }
          .form-dates { color: #666; font-size: 14px; margin-top: 5px; }
          .form-renewal { color: #1e40af; font-size: 12px; }
          .overdue { color: #dc2626; font-weight: bold; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 14px; }
          .contact { margin-top: 20px; background: #e3f2fd; padding: 15px; border-radius: 8px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${centerInfo.centerName || 'Childcare Center'}</h1>
          <h2 style="color: #666; font-size: 18px;">Outstanding Children's Records</h2>
          <p>State Required Documentation</p>
        </div>
        
        <div class="child-info">
          <strong>Child's Name:</strong> ${child.firstName} ${child.lastName}<br>
          <strong>Date Generated:</strong> ${new Date().toLocaleDateString()}
        </div>
        
        <div class="warning">
          <strong>⚠️ Important Notice:</strong><br>
          The following records are outstanding and must be updated as required by the State for your child to remain in childcare.
        </div>
        
        <div class="forms-list">
          <h3>Outstanding Forms:</h3>
          ${formsHtml}
        </div>
        
        <div class="footer">
          <p>Please submit these documents as soon as possible to ensure continued enrollment.</p>
          
          <div class="contact">
            <strong>Questions or Concerns?</strong><br>
            Contact: ${centerInfo.centerName || 'Your Childcare Center'}<br>
            ${centerInfo.contactName ? `${centerInfo.contactName}${centerInfo.contactTitle ? `, ${centerInfo.contactTitle}` : ''}` : ''}<br>
            ${centerInfo.contactPhone ? `Phone: ${centerInfo.contactPhone}` : ''}
          </div>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Prepare individual email confirmation
  const prepareIndividualEmail = (childId) => {
    const child = children.find(c => c.id === childId);
    if (!child) return;

    const outstandingForms = getOutstandingForms(childId);
    const records = childRecords[childId] || {};
    
    // Get parent email from pickup persons
    const parentEmail = child.pickupPersons?.find(p => p.email)?.email;
    
    if (!parentEmail) {
      toast.error('No parent email found for this child');
      return;
    }

    // Set data for confirmation dialog
    setIndividualEmailData({
      childId,
      childName: `${child.firstName} ${child.lastName}`,
      parentEmail,
      outstandingForms: outstandingForms.map(f => {
        const formRecord = getEffectiveFormRecord(f.id, child.category, records[f.id] || {});
        return {
          name: f.name,
          renewalLabel: f.renewalLabel,
          lastSubmitted: formRecord.lastSubmitted || null,
          returnBy: formRecord.returnBy || null
        };
      })
    });
    setShowIndividualEmailConfirm(true);
  };

  // Actually send the individual email after confirmation
  const confirmAndSendIndividualEmail = async () => {
    if (!individualEmailData) return;
    
    setShowIndividualEmailConfirm(false);
    
    try {
      await axios.post(`${API}/children-records/send-email`, {
        childId: individualEmailData.childId,
        childName: individualEmailData.childName,
        parentEmail: individualEmailData.parentEmail,
        outstandingForms: individualEmailData.outstandingForms,
        centerInfo,
        centerNote: individualEmailNote.trim() || null
      });
      
      toast.success(`Email sent to ${individualEmailData.parentEmail}`);
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email');
    } finally {
      setIndividualEmailData(null);
      setIndividualEmailNote("");
    }
  };

  // Mask a phone number for display, e.g. (•••) •••-1234
  const maskPhoneNumber = (phone) => {
    const digits = (phone || '').replace(/\D/g, '');
    const last4 = digits.slice(-4);
    return last4 ? `(•••) •••-${last4}` : 'No phone on file';
  };

  // Mirrors the backend SMS template exactly — used for the live character count preview
  const buildRecordsSmsBody = (childName, count, note) => {
    const trimmedNote = (note || '').trim();
    let body = `ToDays Kidz: ${childName} has ${count} childcare record(s) that need updating. Please check your email or contact the center.`;
    if (trimmedNote) body += ` ${trimmedNote}`;
    body += ' Reply STOP to opt out.';
    return body;
  };

  // Prepare individual SMS confirmation
  const prepareIndividualSms = (childId) => {
    const child = children.find(c => c.id === childId);
    if (!child) return;

    const outstandingForms = getOutstandingForms(childId);
    const parentPhone = child.pickupPersons?.find(p => p.phone)?.phone;

    if (!parentPhone) {
      toast.error('No parent phone number found for this child');
      return;
    }

    setIndividualSmsData({
      childId,
      childName: `${child.firstName} ${child.lastName}`,
      parentPhone,
      outstandingCount: outstandingForms.length
    });
    setIndividualSmsNote("");
    setShowIndividualSmsConfirm(true);
  };

  // Actually send the individual SMS after confirmation
  const confirmAndSendIndividualSms = async () => {
    if (!individualSmsData || smsSendLockRef.current) return;
    smsSendLockRef.current = true;
    setSendingSms(true);
    setShowIndividualSmsConfirm(false);

    try {
      await axios.post(`${API}/children-records/send-sms`, {
        childId: individualSmsData.childId,
        childName: individualSmsData.childName,
        parentPhone: individualSmsData.parentPhone,
        outstandingCount: individualSmsData.outstandingCount,
        centerNote: individualSmsNote.trim() || null
      });

      toast.success(`SMS sent to ${maskPhoneNumber(individualSmsData.parentPhone)}`);
    } catch (error) {
      console.error('Error sending SMS:', error);
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setIndividualSmsData(null);
      setIndividualSmsNote("");
      setSendingSms(false);
      smsSendLockRef.current = false;
    }
  };

  // Bulk email function - prepare preview before sending
  const prepareBulkEmailPreview = () => {
    const childrenWithOutstanding = filteredChildren.filter(child => {
      return selectedChildren.has(child.id) && getOutstandingForms(child.id).length > 0;
    });

    if (childrenWithOutstanding.length === 0) {
      toast.error('No selected children have outstanding forms');
      return;
    }

    // Build preview data
    const previewData = childrenWithOutstanding.map(child => {
      const parentEmail = child.pickupPersons?.find(p => p.email)?.email;
      const outstandingForms = getOutstandingForms(child.id);
      const records = childRecords[child.id] || {};
      
      return {
        childId: child.id,
        childName: `${child.firstName} ${child.lastName}`,
        parentEmail: parentEmail || null,
        hasEmail: !!parentEmail,
        outstandingForms: outstandingForms.map(f => {
          const formRecord = records[f.id] || {};
          return {
            name: f.name,
            renewalLabel: f.renewalLabel,
            returnBy: formRecord.returnBy || null
          };
        })
      };
    });

    setEmailPreviewData(previewData);
    setShowEmailPreview(true);
  };

  // Remove a child from the email preview list
  const removeFromEmailPreview = (childId) => {
    setEmailPreviewData(prev => prev.filter(item => item.childId !== childId));
  };

  // Actually send the bulk emails after preview confirmation
  const confirmAndSendBulkEmails = async () => {
    const childrenToEmail = emailPreviewData.filter(item => item.hasEmail);

    if (childrenToEmail.length === 0) {
      toast.error('No children with valid email addresses');
      setShowEmailPreview(false);
      return;
    }

    setShowEmailPreview(false);
    setBulkEmailSending(true);
    let successCount = 0;
    let failCount = 0;

    for (const item of childrenToEmail) {
      const child = children.find(c => c.id === item.childId);
      if (!child) continue;

      const outstandingForms = getOutstandingForms(child.id);
      const records = childRecords[child.id] || {};

      try {
        await axios.post(`${API}/children-records/send-email`, {
          childId: child.id,
          childName: `${child.firstName} ${child.lastName}`,
          parentEmail: item.parentEmail,
          outstandingForms: outstandingForms.map(f => {
            const formRecord = getEffectiveFormRecord(f.id, child.category, records[f.id] || {});
            return {
              name: f.name,
              renewalLabel: f.renewalLabel,
              lastSubmitted: formRecord.lastSubmitted || null,
              returnBy: formRecord.returnBy || null
            };
          }),
          centerInfo
        });
        successCount++;
        // Small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 600));
      } catch (error) {
        console.error(`Error sending email for ${item.childName}:`, error);
        failCount++;
      }
    }

    setBulkEmailSending(false);
    setSelectedChildren(new Set());
    setEmailPreviewData([]);
    
    if (successCount > 0) {
      toast.success(`Successfully sent ${successCount} email(s)${failCount > 0 ? `, ${failCount} failed` : ''}`);
    } else {
      toast.error('Failed to send emails');
    }
  };

  const filteredChildren = children.filter(child => {
    const fullName = `${child.firstName} ${child.lastName}`.toLowerCase();
    const matchesName = fullName.includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'All' || (child.category || '').toLowerCase() === categoryFilter.toLowerCase();
    return matchesName && matchesCategory;
  });

  const toggleChildSelection = (childId) => {
    setSelectedChildren(prev => {
      const newSet = new Set(prev);
      if (newSet.has(childId)) {
        newSet.delete(childId);
      } else {
        newSet.add(childId);
      }
      return newSet;
    });
  };

  // Print Full Report - shows all children with current and outstanding records
  const printFullReport = () => {
    const childrenToReport = selectedChildren.size > 0 
      ? filteredChildren.filter(c => selectedChildren.has(c.id))
      : filteredChildren.filter(c => getOutstandingForms(c.id).length > 0);

    if (childrenToReport.length === 0) {
      toast.error('No children with outstanding records to print');
      return;
    }

    const today = new Date().toLocaleDateString();
    const totalOutstanding = childrenToReport.reduce((sum, c) => sum + getOutstandingForms(c.id).length, 0);
    
    const childrenHtml = childrenToReport.map(child => {
      const records = childRecords[child.id] || {};
      const outstandingForms = getOutstandingForms(child.id);
      const currentForms = REQUIRED_FORMS.filter(f => {
        const record = getEffectiveFormRecord(f.id, child.category, records[f.id]);
        if (!record?.lastSubmitted || record?.outstanding) return false;
        // N/A forms are current
        if (record.notApplicable || record.lastSubmitted === "N/A") return true;
        // Exclude expired forms — they belong in outstanding
        const expDate = record.expirationDate || calculateExpirationDate(record.lastSubmitted, f.id, child.category);
        if (expDate && new Date(expDate + 'T00:00:00') < new Date()) return false;
        return true;
      });
      
      const outstandingHtml = outstandingForms.map(form => {
        const formRecord = getEffectiveFormRecord(form.id, child.category, records[form.id] || {});
        const isOverdue = formRecord.returnBy && new Date(formRecord.returnBy) < new Date();
        const expirationDate = formRecord.expirationDate || calculateExpirationDate(formRecord.lastSubmitted, form.id, child.category);
        const isExpired = expirationDate && new Date(expirationDate) < new Date();
        return `
          <tr class="outstanding-row">
            <td>☐ ${form.name}</td>
            <td>${form.renewalLabel}</td>
            <td>${formatDate(formRecord.lastSubmitted) || 'Never'}</td>
            <td class="${isExpired ? 'overdue' : ''}">${formatDate(expirationDate) || 'N/A'}${isExpired ? ' (EXPIRED)' : ''}</td>
            <td class="${isOverdue ? 'overdue' : ''}">${formatDate(formRecord.returnBy) || 'Not set'}${isOverdue ? ' (OVERDUE)' : ''}</td>
            <td class="status-outstanding">OUTSTANDING</td>
          </tr>
        `;
      }).join('');

      const currentHtml = currentForms.map(form => {
        const formRecord = getEffectiveFormRecord(form.id, child.category, records[form.id] || {});
        const isNA = formRecord.notApplicable || formRecord.lastSubmitted === "N/A";
        const expirationDate = isNA ? null : (formRecord.expirationDate || calculateExpirationDate(formRecord.lastSubmitted, form.id, child.category));
        const isExpired = expirationDate && new Date(expirationDate) < new Date();
        return `
          <tr class="current-row">
            <td>${isNA ? '—' : '✓'} ${form.name}</td>
            <td>${form.renewalLabel}</td>
            <td>${isNA ? 'N/A' : (formatDate(formRecord.lastSubmitted) || 'Not recorded')}</td>
            <td class="${isExpired ? 'overdue' : ''}">${isNA ? 'N/A' : (formatDate(expirationDate) || 'N/A')}${isExpired ? ' (EXPIRED)' : ''}</td>
            <td>-</td>
            <td class="${isNA ? 'status-na' : 'status-current'}">${isNA ? 'N/A' : 'CURRENT'}</td>
          </tr>
        `;
      }).join('');

      const parentEmail = child.pickupPersons?.find(p => p.email)?.email || 'No email';

      return `
        <div class="child-section">
          <div class="child-header">
            <h3>${child.lastName}, ${child.firstName}</h3>
            <span class="category">${child.category?.replace('_', ' ') || 'Not assigned'}</span>
            <span class="count">${outstandingForms.length} Outstanding</span>
          </div>
          <p class="parent-email">Parent Email: ${parentEmail}</p>
          <table>
            <thead>
              <tr>
                <th>Form Name</th>
                <th>Renewal</th>
                <th>Last Submitted</th>
                <th>Expires</th>
                <th>Return By</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${outstandingHtml}
              ${currentHtml}
            </tbody>
          </table>
        </div>
      `;
    }).join('');

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Children's Records Report - ${centerInfo.centerName}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
          h1 { color: #0d9488; text-align: center; margin-bottom: 5px; }
          h2 { color: #666; text-align: center; font-size: 14px; margin-bottom: 20px; }
          .summary { background: #f0fdfa; border: 1px solid #0d9488; padding: 15px; margin-bottom: 20px; border-radius: 8px; }
          .summary-stats { display: flex; justify-content: space-around; text-align: center; }
          .stat-item { padding: 10px; }
          .stat-number { font-size: 24px; font-weight: bold; color: #0d9488; }
          .stat-label { color: #666; font-size: 11px; }
          .child-section { margin-bottom: 25px; page-break-inside: avoid; border: 1px solid #ddd; border-radius: 8px; overflow: hidden; }
          .child-header { background: #f8fafc; padding: 10px 15px; display: flex; align-items: center; gap: 15px; border-bottom: 1px solid #ddd; }
          .child-header h3 { margin: 0; color: #1e3a5f; font-size: 14px; }
          .category { background: #dbeafe; color: #1e40af; padding: 2px 8px; border-radius: 4px; font-size: 10px; }
          .count { background: #fef3c7; color: #92400e; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: bold; }
          .parent-email { padding: 5px 15px; color: #666; font-size: 11px; margin: 0; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #0d9488; color: white; padding: 8px; text-align: left; font-size: 11px; }
          td { padding: 8px; border-bottom: 1px solid #eee; font-size: 11px; }
          .outstanding-row { background: #fef3c7; }
          .current-row { background: #f0fdf4; }
          .status-outstanding { color: #b45309; font-weight: bold; }
          .status-current { color: #15803d; font-weight: bold; }
          .status-na { color: #6b7280; font-weight: bold; }
          .overdue { color: #dc2626; font-weight: bold; }
          .footer { margin-top: 30px; text-align: center; color: #666; font-size: 10px; border-top: 1px solid #ddd; padding-top: 15px; }
          @media print { 
            body { padding: 10px; } 
            .child-section { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <h1>${centerInfo.centerName || 'Childcare Center'}</h1>
        <h2>Children's Records Report - Generated ${today}</h2>
        
        <div class="summary">
          <div class="summary-stats">
            <div class="stat-item">
              <div class="stat-number">${childrenToReport.length}</div>
              <div class="stat-label">Children</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">${totalOutstanding}</div>
              <div class="stat-label">Total Outstanding</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">${REQUIRED_FORMS.length}</div>
              <div class="stat-label">Required Forms</div>
            </div>
          </div>
        </div>
        
        ${childrenHtml}
        
        <div class="footer">
          <p>${centerInfo.centerName} | ${centerInfo.contactName ? centerInfo.contactName : ''} ${centerInfo.contactTitle ? '- ' + centerInfo.contactTitle : ''}</p>
          <p>${centerInfo.contactPhone ? 'Phone: ' + centerInfo.contactPhone : ''}</p>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  // --- Enrollment Packet Compliance Print helpers ---
  const ENROLL_FORMS_LIST = [
    { key: 'shot_records',         label: 'Child Shot Records' },
    { key: 'enrollment_info',       label: 'Enrollment Information Sheet' },
    { key: 'financial_contract',    label: 'Financial & Contract Agreement' },
    { key: 'about_child',           label: 'All About Your Child Form' },
    { key: 'health_report',         label: 'Child Health Report' },
    { key: 'emergency_consent',     label: 'Emergency Contact / Parental Consent Form' },
    { key: 'transportation_policy', label: 'School Age Transportation & Transfer of Care Policy' },
  ];

  // Denominator = only standard templates with an uploaded file — excludes forms with no template (e.g. School Age if not uploaded)
  const standardTemplateCount = enrollmentTemplates.filter(t => !t.isCustom).length || ENROLL_FORMS_LIST.length;

  // Per-child adjustments — School Age Transportation & Transfer of Care Policy only counts when BOTH:
  //   1) the transportation_policy template is uploaded, AND
  //   2) the child's category is School Age (or after-school).
  // Applies to Form Status list, badge denominators, Complete check, resend modal, and print report.
  const isChildSchoolAge = (child) => {
    const cat = (child?.category || '').toLowerCase();
    return cat.includes('school') || cat === 'after_school';
  };
  const hasTransportationTemplate = enrollmentTemplates.some(t => t.documentType === 'transportation_policy');
  const getApplicableStandardForms = (child) =>
    ENROLL_FORMS_LIST.filter(f =>
      f.key !== 'transportation_policy' || (isChildSchoolAge(child) && hasTransportationTemplate)
    );
  const getEffectiveTemplateCount = (child) =>
    (!isChildSchoolAge(child) && hasTransportationTemplate)
      ? Math.max(0, standardTemplateCount - 1)
      : standardTemplateCount;

  const buildEnrollmentPrintHtml = (childrenList, asOfDate) => {
    const displayDate = asOfDate
      ? new Date(asOfDate + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const centerName = centerInfo.centerName || 'ToDays Kidz';

    const rows = childrenList.map(child => {
      const status = packetStatuses[child.id];
      const applicableForms = getApplicableStandardForms(child);
      const applicableKeys = applicableForms.map(f => f.key);
      // On-file count — same source as the director-facing badge (form_documents),
      // not uploadedDocTypes, so a resend doesn't make a printed summary look incomplete.
      const onFile = (status?.onFileDocTypes || []).filter(k => applicableKeys.includes(k));
      const lastSent = status?.lastSentAt ? new Date(status.lastSentAt).toLocaleDateString() : '—';
      const effectiveTotal = applicableKeys.length;
      const totalReceived = onFile.length;
      const totalRequired = effectiveTotal;

      let statusLabel, statusClass;
      if (!status) { statusLabel = 'Not Sent'; statusClass = 'badge-gray'; }
      else if (totalReceived === 0) { statusLabel = 'Waiting'; statusClass = 'badge-yellow'; }
      else if (totalReceived >= effectiveTotal) { statusLabel = 'All Forms Received'; statusClass = 'badge-green'; }
      else { statusLabel = `${totalReceived} of ${totalRequired} on file`; statusClass = 'badge-blue'; }

      const formRows = applicableForms.map(f => {
        const received = onFile.includes(f.key);
        return `<tr>
          <td class="form-name">${received ? '&#10003;' : '&mdash;'}&nbsp;&nbsp;${f.label}</td>
          <td class="${received ? 'status-received' : 'status-pending'}">${received ? 'Received' : 'Pending'}</td>
        </tr>`;
      }).join('');

      const customTemplates = enrollmentTemplates.filter(t => t.isCustom);
      const customRows = customTemplates.length > 0 ? customTemplates.map(tmpl => {
        const done = !!(status?.customFormStatus?.[tmpl.customId]);
        return `<tr>
          <td class="form-name form-custom">${done ? '&#10003;' : '&mdash;'}&nbsp;&nbsp;${tmpl.customLabel} <span class="form-type">(${tmpl.returnRequired ? 'Return Required' : 'Acknowledge Only'})</span></td>
          <td class="${done ? 'status-received' : 'status-pending'}">${done ? 'Received' : 'Pending'}</td>
        </tr>`;
      }).join('') : '';

      return `
        <div class="child-block">
          <div class="child-header">
            <span class="child-name">${child.name || `${child.firstName || ''} ${child.lastName || ''}`.trim()}</span>
            <span class="badge ${statusClass}">${statusLabel}</span>
            <span class="sent-date">Packet sent: ${lastSent}</span>
          </div>
          <table class="form-table">
            <thead><tr><th>Form</th><th>Status</th></tr></thead>
            <tbody>
              ${formRows}
              ${customRows ? `<tr><td colspan="2" class="section-label">Additional Center Forms</td></tr>${customRows}` : ''}
            </tbody>
          </table>
        </div>`;
    }).join('');

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Enrollment Packet Compliance Report — ${centerName}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    @page { size: letter portrait; margin: 0.75in 0.75in 0.75in 0.75in; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #1a1a1a; background: #fff; }
    .report-header { border-bottom: 2px solid #0d9488; padding-bottom: 10px; margin-bottom: 16px; display: flex; justify-content: space-between; align-items: flex-end; }
    .report-title { font-size: 18px; font-weight: bold; color: #0d9488; }
    .report-subtitle { font-size: 11px; color: #555; margin-top: 2px; }
    .report-meta { text-align: right; font-size: 10px; color: #666; line-height: 1.6; }
    .child-block { border: 1px solid #d1d5db; border-radius: 6px; margin-bottom: 14px; overflow: hidden; page-break-inside: avoid; }
    .child-header { background: #f0fdfa; border-bottom: 1px solid #d1d5db; padding: 7px 12px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
    .child-name { font-size: 13px; font-weight: bold; color: #134e4a; flex: 1; }
    .badge { font-size: 9px; font-weight: bold; padding: 2px 8px; border-radius: 10px; text-transform: uppercase; letter-spacing: 0.04em; }
    .badge-gray   { background: #f3f4f6; color: #6b7280; border: 1px solid #d1d5db; }
    .badge-yellow { background: #fef9c3; color: #854d0e; border: 1px solid #fde047; }
    .badge-blue   { background: #dbeafe; color: #1d4ed8; border: 1px solid #93c5fd; }
    .badge-green  { background: #dcfce7; color: #166534; border: 1px solid #86efac; }
    .sent-date { font-size: 9px; color: #6b7280; margin-left: auto; }
    .form-table { width: 100%; border-collapse: collapse; }
    .form-table th { background: #f8fafc; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: #374151; padding: 5px 12px; border-bottom: 1px solid #e5e7eb; text-align: left; }
    .form-table td { padding: 4px 12px; border-bottom: 1px solid #f3f4f6; font-size: 10.5px; }
    .form-table tr:last-child td { border-bottom: none; }
    .form-name { color: #374151; width: 78%; }
    .form-custom { color: #6b7280; }
    .form-type { font-size: 9px; color: #9ca3af; }
    .status-received { color: #15803d; font-weight: 600; width: 22%; }
    .status-pending  { color: #9ca3af; width: 22%; }
    .section-label { font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: #9ca3af; padding: 6px 12px 3px; background: #fafafa; border-bottom: 1px solid #f3f4f6; }
    .report-footer { margin-top: 20px; border-top: 1px solid #e5e7eb; padding-top: 10px; text-align: center; font-size: 9px; color: #9ca3af; }
    @media print {
      body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
      .child-block { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="report-header">
    <div>
      <div class="report-title">${centerName}</div>
      <div class="report-subtitle">Enrollment Packet Compliance Report</div>
    </div>
    <div class="report-meta">
      Printed: ${displayDate}<br/>
      Children: ${childrenList.length}<br/>
      For State Compliance Review
    </div>
  </div>
  ${rows}
  <div class="report-footer">
    Printed for state compliance review &mdash; ${centerName} &mdash; ${displayDate}
  </div>
</body>
</html>`;
  };

  const firePrint = (target, asOfDate) => {
    const list = target === 'all'
      ? children.filter(c => !!packetStatuses[c.id])
      : [target];
    const html = buildEnrollmentPrintHtml(list, asOfDate);
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    w.onload = () => w.print();
  };

  const printEnrollmentSummary = (child) => {
    setPendingPrintTarget(child);
    setPrintAsOfDate(new Date().toISOString().split('T')[0]);
    setShowPrintDatePicker(true);
  };

  const printAllEnrollmentSummary = () => {
    const sent = children.filter(c => !!packetStatuses[c.id]);
    if (sent.length === 0) {
      toast.error('No enrollment packets have been sent yet');
      return;
    }
    setPendingPrintTarget('all');
    setPrintAsOfDate(new Date().toISOString().split('T')[0]);
    setShowPrintDatePicker(true);
  };
  // --- End Enrollment Packet print helpers ---

  // Download Full Report as PDF
  const downloadFullReportPDF = async () => {
    const childrenToReport = selectedChildren.size > 0 
      ? filteredChildren.filter(c => selectedChildren.has(c.id))
      : filteredChildren.filter(c => getOutstandingForms(c.id).length > 0);

    if (childrenToReport.length === 0) {
      toast.error('No children with outstanding records to export');
      return;
    }

    try {
      toast.info('Generating PDF report...');
      
      const reportData = childrenToReport.map(child => {
        const records = childRecords[child.id] || {};
        const outstandingForms = getOutstandingForms(child.id);
        const currentForms = REQUIRED_FORMS.filter(f => {
          const record = getEffectiveFormRecord(f.id, child.category, records[f.id]);
          if (!record?.lastSubmitted || record?.outstanding) return false;
          if (record.notApplicable || record.lastSubmitted === "N/A") return true;
          const expDate = record.expirationDate || calculateExpirationDate(record.lastSubmitted, f.id, child.category);
          if (expDate && new Date(expDate + 'T00:00:00') < new Date()) return false;
          return true;
        });
        
        return {
          childId: child.id,
          childName: `${child.lastName}, ${child.firstName}`,
          category: child.category?.replace('_', ' ') || 'Not assigned',
          parentEmail: child.pickupPersons?.find(p => p.email)?.email || null,
          outstandingForms: outstandingForms.map(f => {
            const rec = getEffectiveFormRecord(f.id, child.category, records[f.id]);
            return {
              name: f.name,
              renewalLabel: f.renewalLabel,
              lastSubmitted: rec?.lastSubmitted || null,
              returnBy: rec?.returnBy || null
            };
          }),
          currentForms: currentForms.map(f => {
            const rec = getEffectiveFormRecord(f.id, child.category, records[f.id] || {});
            const isNA = rec.notApplicable || rec.lastSubmitted === "N/A";
            return {
              name: f.name,
              renewalLabel: f.renewalLabel,
              lastSubmitted: isNA ? "N/A" : (rec.lastSubmitted || null),
              isNA: isNA
            };
          })
        };
      });

      const response = await axios.post(`${API}/children-records/generate-full-report-pdf`, {
        children: reportData,
        centerInfo,
        generatedDate: new Date().toISOString()
      }, { responseType: 'blob' });

      // Download the PDF
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Children_Records_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF report downloaded');
    } catch (error) {
      console.error('Error generating PDF report:', error);
      toast.error('Failed to generate PDF. Using print view instead.');
      // Fallback to print
      printFullReport();
    }
  };

  if (loading) {
    return (
      <Card className="bg-white/90 backdrop-blur-sm border-2 border-teal-200">
        <CardContent className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-teal-700">Loading children's records...</p>
        </CardContent>
      </Card>
    );
  }

  // Group doc notifications by child for collapsible display
  const notifGrouped = docNotifications.reduce((acc, n) => {
    if (!acc[n.childId]) acc[n.childId] = { childName: n.childName, docs: [] };
    acc[n.childId].docs.push(n);
    return acc;
  }, {});
  const notifChildGroups = Object.entries(notifGrouped);
  const docTypeLabel = (t) => ({
    shot_records: 'Shot Records',
    enrollment_info: 'Enrollment Information Sheet',
    financial_contract: 'Financial & Contract Agreement',
    about_child: 'All About Your Child Form',
    health_report: 'Child Health Report',
    emergency_consent: 'Emergency Contact/Parental Consent Form',
    transportation_policy: 'School Age Transportation & Transfer of Care Policy'
  })[t] || t.replace(/_/g, ' ');

  return (
    <Card className="bg-white/90 backdrop-blur-sm border-2 border-teal-200">
      <CardHeader className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-t-lg">
        <CardTitle className="flex items-center gap-2 text-xl">
          <ClipboardList className="w-6 h-6" />
          Children's Records - Outstanding Documents
        </CardTitle>
      </CardHeader>
      
      <CardContent className="p-6">
        <Tabs defaultValue={initialTopTab} className="w-full">
          <TabsList className={`grid w-full ${enrollmentPermitted ? 'grid-cols-3' : 'grid-cols-2'} mb-6 bg-transparent gap-2 p-0 h-auto`}>
            <TabsTrigger value="records" className="flex items-center gap-2 border border-teal-200 text-teal-600 bg-white rounded-lg py-2 font-medium shadow-md hover:shadow-lg hover:bg-teal-50 transition-all data-[state=active]:bg-teal-500 data-[state=active]:text-white data-[state=active]:border-teal-400 data-[state=active]:shadow-lg">
              <FileText className="w-4 h-4" />
              Records Management
            </TabsTrigger>
            {enrollmentPermitted && (
              <TabsTrigger value="enrollment" className="flex items-center gap-2 border border-teal-200 text-teal-600 bg-white rounded-lg py-2 font-medium shadow-md hover:shadow-lg hover:bg-teal-50 transition-all data-[state=active]:bg-teal-500 data-[state=active]:text-white data-[state=active]:border-teal-400 data-[state=active]:shadow-lg">
                <ClipboardList className="w-4 h-4" />
                Enrollment Packet
              </TabsTrigger>
            )}
            <TabsTrigger value="reminders" className="flex items-center gap-2 border border-teal-200 text-teal-600 bg-white rounded-lg py-2 font-medium shadow-md hover:shadow-lg hover:bg-teal-50 transition-all data-[state=active]:bg-teal-500 data-[state=active]:text-white data-[state=active]:border-teal-400 data-[state=active]:shadow-lg">
              <Bell className="w-4 h-4" />
              Automated Reminders
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="records" className="space-y-6">
        {/* ── Stage 4: 6-Month Review Tracker ─────────────────────────── */}
        {(() => {
          const resubmitted = sixMonthNotifications.filter(r => r.status === 'resubmitted');
          const sent        = sixMonthNotifications.filter(r => r.status === 'sent');
          const pendingCt   = resubmitted.length;
          return (
            <div className="border border-green-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setTrackerOpen(o => !o)}
                className="w-full flex items-center justify-between px-4 py-3 bg-green-50 hover:bg-green-100"
                data-testid="six-month-tracker-toggle"
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-green-800">Emergency Contact Renewal Tracker</span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          className="text-gray-400 hover:text-teal-600 flex-shrink-0 cursor-default"
                          data-testid="ec-tracker-tooltip"
                        >
                          <Info className="w-3.5 h-3.5" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-center leading-relaxed">
                        Tracks when Emergency Contact / Parental Consent forms are due for renewal. Reminders are automatically sent to parents at your configured interval.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {pendingCt > 0 && (
                    <span className="text-xs bg-teal-100 text-teal-700 border border-teal-200 px-2 py-0.5 rounded-full font-medium">
                      {pendingCt} Resubmitted
                    </span>
                  )}
                  {sent.length > 0 && (
                    <span className="text-xs bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-medium">
                      {sent.length} Awaiting
                    </span>
                  )}
                </div>
                {trackerOpen ? <ChevronUp className="w-4 h-4 text-green-600" /> : <ChevronDown className="w-4 h-4 text-green-600" />}
              </button>
              {trackerOpen && (
                <div className="border-t border-gray-100">
                  {/* Settings row */}
                  <div className="flex items-center gap-4 px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex-wrap">
                    {/* Enable / Disable toggle */}
                    <label className="flex items-center gap-2 cursor-pointer select-none" title={sixMonthEnabled ? 'Click to disable tracker' : 'Click to enable tracker'}>
                      <div
                        className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${sixMonthEnabled ? 'bg-teal-500' : 'bg-gray-300'}`}
                        onClick={() => saveTrackerSettings({ enabled: !sixMonthEnabled })}
                        data-testid="six-month-tracker-enabled-toggle"
                      >
                        <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${sixMonthEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                      </div>
                      <span className={`text-xs font-medium ${sixMonthEnabled ? 'text-teal-700' : 'text-gray-400'}`}>
                        {sixMonthEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </label>
                    <div className={`flex items-center gap-2 ${!sixMonthEnabled ? 'opacity-40 pointer-events-none' : ''}`}>
                      <span className="text-xs text-gray-500">Review Interval:</span>
                      <input
                        type="number" min="1" max="24"
                        value={sixMonthInterval}
                        onChange={e => setSixMonthInterval(Number(e.target.value))}
                        onBlur={e => saveTrackerSettings({ interval: e.target.value })}
                        onKeyDown={e => e.key === 'Enter' && saveTrackerSettings({ interval: e.target.value })}
                        className="w-12 text-xs border border-gray-200 rounded px-1.5 py-0.5 text-center focus:outline-none focus:border-teal-400 bg-white"
                        data-testid="six-month-interval-input"
                        disabled={!sixMonthEnabled}
                      />
                      <span className="text-xs text-gray-400">months (press Enter or click away to save)</span>
                    </div>
                  </div>
                  {!sixMonthEnabled && (
                    <div className="px-4 py-4 text-center text-xs text-gray-400 bg-gray-50 border-b border-gray-100">
                      Tracker is disabled — no reminders will be sent. Toggle on above to re-enable.
                    </div>
                  )}
                  {/* Resubmitted inbox */}
                  {resubmitted.length === 0 && sent.length === 0 && (
                    <div className="px-4 py-6 text-center text-xs text-gray-400">
                      No review activity yet. Reminders will be sent automatically when forms reach the review interval.
                    </div>
                  )}
                  {resubmitted.length > 0 && (
                    <div>
                      {/* Section header with Select All + Print All */}
                      <div className="flex items-center justify-between px-4 pt-3 pb-1">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Resubmitted — Ready to Print & File</p>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              if (selectedReviews.size === resubmitted.length) {
                                setSelectedReviews(new Set());
                              } else {
                                setSelectedReviews(new Set(resubmitted.map(r => r.id)));
                              }
                            }}
                            className="text-xs text-teal-600 hover:text-teal-800 font-medium"
                            data-testid="tracker-select-all"
                          >
                            {selectedReviews.size === resubmitted.length ? 'Deselect All' : 'Select All'}
                          </button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-1"
                            onClick={() => {
                              const toPrint = selectedReviews.size > 0
                                ? resubmitted.filter(r => selectedReviews.has(r.id))
                                : resubmitted;
                              openTrackerPrint(toPrint);
                            }}
                            data-testid="tracker-print-all"
                          >
                            <Printer className="w-3 h-3" />
                            {selectedReviews.size > 0 ? `Print All Selected (${selectedReviews.size})` : 'Print All'}
                          </Button>
                        </div>
                      </div>
                      {resubmitted.map(r => (
                        <div key={r.id} className={`flex items-center justify-between px-4 py-2.5 border-b border-gray-50 hover:bg-gray-50 ${selectedReviews.has(r.id) ? 'bg-teal-50' : 'bg-white'}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            <input
                              type="checkbox"
                              checked={selectedReviews.has(r.id)}
                              onChange={() => setSelectedReviews(prev => {
                                const next = new Set(prev);
                                next.has(r.id) ? next.delete(r.id) : next.add(r.id);
                                return next;
                              })}
                              className="w-4 h-4 accent-teal-600 cursor-pointer flex-shrink-0"
                              data-testid={`tracker-checkbox-${r.id}`}
                            />
                            <div>
                              <p className="text-sm font-medium text-gray-800">{r.childName}</p>
                              <p className="text-xs text-gray-400">Emergency Contact</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {/* Editable resubmitted date */}
                            {editingReviewDate?.id === r.id ? (
                              <div className="flex items-center gap-1">
                                <input
                                  type="date"
                                  value={editingReviewDate.value}
                                  onChange={e => setEditingReviewDate(prev => ({ ...prev, value: e.target.value }))}
                                  className="text-xs border border-teal-300 rounded px-1.5 py-0.5 focus:outline-none"
                                  data-testid={`edit-review-date-${r.id}`}
                                />
                                <button onClick={() => handleSaveReviewDate(r.id, editingReviewDate.value)} className="text-xs bg-teal-600 text-white px-2 py-0.5 rounded hover:bg-teal-700">Save</button>
                                <button onClick={() => setEditingReviewDate(null)} className="text-xs text-gray-400 hover:text-gray-600">✕</button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setEditingReviewDate({ id: r.id, value: r.resubmittedAt || '' })}
                                className="text-xs text-gray-500 hover:text-teal-600 flex items-center gap-1"
                                data-testid={`review-date-btn-${r.id}`}
                                title="Click to edit date"
                              >
                                {r.resubmittedAt ? new Date(r.resubmittedAt + 'T00:00:00').toLocaleDateString() : '—'} ✎
                              </button>
                            )}
                            <button onClick={() => handlePrintReview(r)} className="text-xs bg-white text-gray-700 border border-gray-200 px-2.5 py-1 rounded hover:bg-gray-50" data-testid={`print-review-${r.id}`}>Print</button>
                            <button onClick={() => handleArchiveReview(r.id)} className="text-xs bg-white text-gray-600 border border-gray-200 px-2.5 py-1 rounded hover:bg-gray-50" data-testid={`archive-review-${r.id}`}>Archive</button>
                            <button onClick={() => handleDeleteReview(r.id)} className="text-xs bg-white text-red-500 border border-red-100 px-2.5 py-1 rounded hover:bg-red-50" data-testid={`delete-review-${r.id}`}>Delete</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {sent.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 pt-3 pb-1">Reminders Sent — Awaiting Resubmission</p>
                      {sent.map(r => (
                        <div key={r.id} className="flex items-center justify-between px-4 py-2.5 border-b border-gray-50 bg-amber-50">
                          <div>
                            <p className="text-sm font-medium text-gray-800">{r.childName}</p>
                            <p className="text-xs text-amber-600">Emergency Contact — reminder sent {r.noticeSentAt ? new Date(r.noticeSentAt + 'T00:00:00').toLocaleDateString() : ''}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}
        {/* ── Financial Contract Renewal Tracker ──────────────────────── */}
        {(() => {
          const faResubmitted = financialNotifications.filter(r => r.status === 'resubmitted');
          const faSent        = financialNotifications.filter(r => r.status === 'sent');
          const faPendingCt   = faResubmitted.length;
          return (
            <div className="border border-blue-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setFinancialTrackerOpen(o => !o)}
                className="w-full flex items-center justify-between px-4 py-3 bg-blue-50 hover:bg-blue-100"
                data-testid="financial-tracker-toggle"
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-blue-800">Financial Contract Renewal Tracker</span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-gray-400 hover:text-teal-600 flex-shrink-0 cursor-default" data-testid="fa-tracker-tooltip">
                          <Info className="w-3.5 h-3.5" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-center leading-relaxed">
                        Tracks when Financial Contract agreements are due for renewal. Reminders are automatically sent to parents at your configured interval.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {faPendingCt > 0 && (
                    <span className="text-xs bg-teal-100 text-teal-700 border border-teal-200 px-2 py-0.5 rounded-full font-medium">
                      {faPendingCt} Resubmitted
                    </span>
                  )}
                  {faSent.length > 0 && (
                    <span className="text-xs bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-medium">
                      {faSent.length} Awaiting
                    </span>
                  )}
                </div>
                {financialTrackerOpen ? <ChevronUp className="w-4 h-4 text-blue-600" /> : <ChevronDown className="w-4 h-4 text-blue-600" />}
              </button>
              {financialTrackerOpen && (
                <div className="border-t border-gray-100">
                  {/* Settings row */}
                  <div className="flex items-center gap-4 px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex-wrap">
                    <label className="flex items-center gap-2 cursor-pointer select-none" title={financialEnabled ? 'Click to disable tracker' : 'Click to enable tracker'}>
                      <div
                        className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${financialEnabled ? 'bg-teal-500' : 'bg-gray-300'}`}
                        onClick={() => saveFinancialTrackerSettings({ enabled: !financialEnabled })}
                        data-testid="financial-tracker-enabled-toggle"
                      >
                        <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${financialEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                      </div>
                      <span className={`text-xs font-medium ${financialEnabled ? 'text-teal-700' : 'text-gray-400'}`}>
                        {financialEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </label>
                    <div className={`flex items-center gap-2 ${!financialEnabled ? 'opacity-40 pointer-events-none' : ''}`}>
                      <span className="text-xs text-gray-500">Review Interval:</span>
                      <input
                        type="number" min="1" max="24"
                        value={financialInterval}
                        onChange={e => setFinancialInterval(Number(e.target.value))}
                        onBlur={e => saveFinancialTrackerSettings({ interval: e.target.value })}
                        onKeyDown={e => e.key === 'Enter' && saveFinancialTrackerSettings({ interval: e.target.value })}
                        className="w-12 text-xs border border-gray-200 rounded px-1.5 py-0.5 text-center focus:outline-none focus:border-teal-400 bg-white"
                        data-testid="financial-interval-input"
                        disabled={!financialEnabled}
                      />
                      <span className="text-xs text-gray-400">months (press Enter or click away to save)</span>
                    </div>
                  </div>
                  {!financialEnabled && (
                    <div className="px-4 py-4 text-center text-xs text-gray-400 bg-gray-50 border-b border-gray-100">
                      Tracker is disabled — no reminders will be sent. Toggle on above to re-enable.
                    </div>
                  )}
                  {faResubmitted.length === 0 && faSent.length === 0 && (
                    <div className="px-4 py-6 text-center text-xs text-gray-400">
                      No review activity yet. Reminders will be sent automatically when Financial Contracts reach the review interval.
                    </div>
                  )}
                  {faResubmitted.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between px-4 pt-3 pb-1">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Resubmitted — Ready to Print & File</p>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              if (selectedFinancialReviews.size === faResubmitted.length) {
                                setSelectedFinancialReviews(new Set());
                              } else {
                                setSelectedFinancialReviews(new Set(faResubmitted.map(r => r.id)));
                              }
                            }}
                            className="text-xs text-teal-600 hover:text-teal-800 font-medium"
                            data-testid="fa-tracker-select-all"
                          >
                            {selectedFinancialReviews.size === faResubmitted.length ? 'Deselect All' : 'Select All'}
                          </button>
                          <Button size="sm" variant="outline"
                            className="h-7 text-xs border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-1"
                            onClick={() => {
                              const toPrint = selectedFinancialReviews.size > 0
                                ? faResubmitted.filter(r => selectedFinancialReviews.has(r.id))
                                : faResubmitted;
                              openTrackerPrint(toPrint);
                            }}
                            data-testid="fa-tracker-print-all"
                          >
                            <Printer className="w-3 h-3" />
                            {selectedFinancialReviews.size > 0 ? `Print All Selected (${selectedFinancialReviews.size})` : 'Print All'}
                          </Button>
                        </div>
                      </div>
                      {faResubmitted.map(r => (
                        <div key={r.id} className={`flex items-center justify-between px-4 py-2.5 border-b border-gray-50 hover:bg-gray-50 ${selectedFinancialReviews.has(r.id) ? 'bg-teal-50' : 'bg-white'}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            <input
                              type="checkbox"
                              checked={selectedFinancialReviews.has(r.id)}
                              onChange={() => setSelectedFinancialReviews(prev => {
                                const next = new Set(prev);
                                next.has(r.id) ? next.delete(r.id) : next.add(r.id);
                                return next;
                              })}
                              className="w-4 h-4 accent-teal-600 cursor-pointer flex-shrink-0"
                              data-testid={`fa-checkbox-${r.id}`}
                            />
                            <div>
                              <p className="text-sm font-medium text-gray-800">{r.childName}</p>
                              <p className="text-xs text-gray-400">Financial Contract</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {editingFinancialDate?.id === r.id ? (
                              <div className="flex items-center gap-1">
                                <input
                                  type="date"
                                  value={editingFinancialDate.value}
                                  onChange={e => setEditingFinancialDate(prev => ({ ...prev, value: e.target.value }))}
                                  className="text-xs border border-teal-300 rounded px-1.5 py-0.5 focus:outline-none"
                                  data-testid={`edit-fa-date-${r.id}`}
                                />
                                <button onClick={() => handleSaveFinancialReviewDate(r.id, editingFinancialDate.value)} className="text-xs bg-teal-600 text-white px-2 py-0.5 rounded hover:bg-teal-700">Save</button>
                                <button onClick={() => setEditingFinancialDate(null)} className="text-xs text-gray-400 hover:text-gray-600">✕</button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setEditingFinancialDate({ id: r.id, value: r.resubmittedAt || '' })}
                                className="text-xs text-gray-500 hover:text-teal-600 flex items-center gap-1"
                                data-testid={`fa-date-btn-${r.id}`}
                                title="Click to edit date"
                              >
                                {r.resubmittedAt ? new Date(r.resubmittedAt + 'T00:00:00').toLocaleDateString() : '—'} ✎
                              </button>
                            )}
                            <button onClick={() => handlePrintFinancialReview(r)} className="text-xs bg-white text-gray-700 border border-gray-200 px-2.5 py-1 rounded hover:bg-gray-50" data-testid={`fa-print-${r.id}`}>Print</button>
                            <button onClick={() => handleArchiveFinancialReview(r.id)} className="text-xs bg-white text-gray-600 border border-gray-200 px-2.5 py-1 rounded hover:bg-gray-50" data-testid={`fa-archive-${r.id}`}>Archive</button>
                            <button onClick={() => handleDeleteFinancialReview(r.id)} className="text-xs bg-white text-red-500 border border-red-100 px-2.5 py-1 rounded hover:bg-red-50" data-testid={`fa-delete-${r.id}`}>Delete</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {faSent.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 pt-3 pb-1">Reminders Sent — Awaiting Resubmission</p>
                      {faSent.map(r => (
                        <div key={r.id} className="flex items-center justify-between px-4 py-2.5 border-b border-gray-50 bg-amber-50">
                          <div>
                            <p className="text-sm font-medium text-gray-800">{r.childName}</p>
                            <p className="text-xs text-amber-600">Financial Contract — reminder sent {r.noticeSentAt ? new Date(r.noticeSentAt + 'T00:00:00').toLocaleDateString() : ''}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}
        {/* Document Upload Notifications — always visible; shows empty-state when none */}
        <div className="bg-green-50 rounded-lg border-2 border-green-300">
            {/* Outer header — always visible, click to collapse */}
            <div
              className="flex items-center justify-between p-4 cursor-pointer select-none"
              onClick={() => setIsDocsPanelOpen(o => !o)}
            >
              <h3 className="font-semibold text-green-800 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                New Documents Uploaded{notifChildGroups.length > 0 ? ` (${notifChildGroups.length} ${notifChildGroups.length === 1 ? 'child' : 'children'})` : ''}
              </h3>
              {isDocsPanelOpen
                ? <ChevronUp className="w-4 h-4 text-green-600" />
                : <ChevronDown className="w-4 h-4 text-green-600" />}
            </div>
            {isDocsPanelOpen && (
            <div className="px-4 pb-4 space-y-2">
              {notifChildGroups.length === 0 && (
                <p className="text-sm text-green-700 bg-white rounded border border-green-200 px-3 py-3" data-testid="docs-panel-empty-state">
                  No documents are waiting for review. Parent uploads and replacement forms will appear here when submitted.
                </p>
              )}
              {notifChildGroups.map(([childId, { childName, docs }]) => {
                const isOpen = expandedNotifChildren.has(childId);
                return (
                  <div key={childId} className="bg-white rounded border border-green-200 overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between px-3 py-2 hover:bg-green-50 transition-colors"
                      onClick={() => setExpandedNotifChildren(prev => {
                        const next = new Set(prev);
                        next.has(childId) ? next.delete(childId) : next.add(childId);
                        return next;
                      })}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center">
                          <FileText className="w-3.5 h-3.5 text-green-600" />
                        </div>
                        <span className="font-medium text-gray-800">{childName}</span>
                        <span className="text-xs bg-green-100 text-green-700 font-semibold px-2 py-0.5 rounded-full">
                          {docs.length} {docs.length === 1 ? 'document' : 'documents'}
                        </span>
                      </div>
                      {isOpen ? <ChevronUp className="w-4 h-4 text-green-600" /> : <ChevronDown className="w-4 h-4 text-green-600" />}
                    </button>
                    {isOpen && (
                      <div className="border-t border-green-100 divide-y divide-green-50">
                        {docs.map(notification => (
                          <div key={notification.id} className="flex items-center justify-between px-3 py-2 bg-white hover:bg-green-50">
                            <div className="flex items-center gap-3">
                              <div className="w-6 h-6 bg-green-50 rounded-full flex items-center justify-center">
                                <FileText className="w-3 h-3 text-green-500" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-800 flex items-center gap-2">
                                  {docTypeLabel(notification.documentType)}
                                  {notification.pendingReplacement === true && (
                                    <span className="text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full" data-testid={`doc-notification-pending-badge-${notification.id}`}>
                                      Replacement · Pending Review
                                    </span>
                                  )}
                                </p>
                                <p className="text-xs text-gray-500">{new Date(notification.uploadedAt).toLocaleDateString()}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button size="sm" variant="ghost" className="text-xs h-7 px-2"
                                data-testid={`doc-notification-view-${notification.id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  viewFormDocument(notification.childId, notification.documentType);
                                }}>
                                View
                              </Button>
                              <Button size="sm" variant="ghost" className="text-xs h-7 px-2 text-gray-500"
                                onClick={() => clearNotification(notification.id)}>
                                ✓ Clear
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {/* Clear All — inside expanded section to prevent accidental clicks */}
              {notifChildGroups.length > 0 && (
              <div className="flex justify-end pt-1">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50 text-xs"
                  onClick={() => setShowClearAllConfirm(true)}
                  data-testid="docs-panel-clear-all"
                >
                  Clear All Notifications
                </Button>
              </div>
              )}
            </div>
            )}
          </div>
        
        {/* Center Contact Info Section */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Center Contact Information (for printed forms)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-sm text-blue-700">Center Name</Label>
              <Input
                value={centerInfo.centerName}
                readOnly
                className="mt-1 bg-gray-100 cursor-not-allowed"
                title="Center name is set from your organization settings"
              />
              <p className="text-xs text-gray-500 mt-1">From organization settings</p>
            </div>
            <div>
              <Label className="text-sm text-blue-700">Contact Name</Label>
              <Input
                value={centerInfo.contactName}
                onChange={(e) => setCenterInfo(prev => ({ ...prev, contactName: e.target.value }))}
                placeholder="Contact person..."
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm text-blue-700">Title</Label>
              <Input
                value={centerInfo.contactTitle}
                onChange={(e) => setCenterInfo(prev => ({ ...prev, contactTitle: e.target.value }))}
                placeholder="Director, etc..."
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm text-blue-700">Phone Number</Label>
              <Input
                value={centerInfo.contactPhone}
                onChange={(e) => setCenterInfo(prev => ({ 
                  ...prev, 
                  contactPhone: formatPhoneNumber(e.target.value) 
                }))}
                placeholder="(xxx) xxx-xxxx"
                className="mt-1"
                maxLength={14}
              />
            </div>
          </div>
          <Button
            onClick={saveCenterInfo}
            size="sm"
            className="mt-3 bg-blue-600 hover:bg-blue-700"
          >
            <Save className="w-4 h-4 mr-1" />
            Save Contact Info
          </Button>
        </div>

        {/* Bulk Upload for ACTION Section */}
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <div className="bg-white p-3 rounded-lg border border-purple-100 mb-4">
            <div className="flex items-start gap-2">
              <Info className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-purple-700">
                <p className="font-medium mb-1">Bulk Upload for ACTION - Select Children with Outstanding Records:</p>
                <ol className="list-decimal list-inside space-y-1 text-purple-600">
                  <li>Check individual children below, or click <strong>"Select Children with Outstanding Records"</strong> or <strong>"Select All"</strong></li>
                  <li>Set a <strong>"Return By Date"</strong> and click <strong>"Apply to Selected"</strong> or <strong>"Apply to ALL"</strong> to update all forms at once</li>
                  <li>Click <strong>"Preview & Email"</strong> to review what will be sent before emailing parents</li>
                </ol>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <h3 className="font-semibold text-purple-800 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Bulk Upload for ACTION
                </h3>
                <span className="text-sm text-purple-600">
                  {selectedChildren.size > 0 ? `${selectedChildren.size} selected` : 'Select children below'}
                </span>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => {
                    const childrenWithOutstanding = filteredChildren.filter(c => getOutstandingForms(c.id).length > 0);
                    setSelectedChildren(new Set(childrenWithOutstanding.map(c => c.id)));
                  }}
                  size="sm" variant="outline"
                  className="border-purple-300 text-purple-700 hover:bg-purple-100"
                >
                  <CheckSquare className="w-4 h-4 mr-1" />
                  Select Children with Outstanding Records
                </Button>
                <Button
                  onClick={() => setSelectedChildren(new Set(filteredChildren.map(c => c.id)))}
                  size="sm" variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                >
                  <CheckSquare className="w-4 h-4 mr-1" />
                  Select All
                </Button>
                {selectedChildren.size > 0 && (
                  <Button onClick={() => setSelectedChildren(new Set())} size="sm" variant="outline" className="border-gray-300">
                    Clear Selection
                  </Button>
                )}
                <Button
                  onClick={prepareBulkEmailPreview}
                  size="sm"
                  disabled={selectedChildren.size === 0 || bulkEmailSending}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  {bulkEmailSending ? 'Sending...' : `Preview & Email (${selectedChildren.size})`}
                </Button>
                <Button onClick={printFullReport} size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50">
                  <Printer className="w-4 h-4 mr-1" />
                  Print Report
                </Button>
                <Button onClick={downloadFullReportPDF} size="sm" variant="outline" className="border-green-300 text-green-700 hover:bg-green-50">
                  <Download className="w-4 h-4 mr-1" />
                  Download PDF
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-3 pt-2 border-t border-purple-200 flex-wrap">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-purple-600" />
                <Label className="text-sm text-purple-700 font-medium whitespace-nowrap">Set Return By Date:</Label>
                <div className="flex items-center gap-1">
                  <Input type="date" value={globalReturnByDate} onChange={(e) => setGlobalReturnByDate(e.target.value)} className="w-40 h-8 text-sm" />
                  {globalReturnByDate && (
                    <Button onClick={() => setGlobalReturnByDate('')} size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-red-500 hover:bg-red-50">
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              <Button
                onClick={applyGlobalReturnByToAllSelected}
                size="sm"
                disabled={!globalReturnByDate || selectedChildren.size === 0 || saving}
                className={!globalReturnByDate || selectedChildren.size === 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-amber-600 hover:bg-amber-700 text-white'}
              >
                <Calendar className="w-4 h-4 mr-1" />
                {saving ? 'Applying...' : 'Apply to Selected'}
              </Button>
              <Button
                onClick={applyGlobalReturnByToAllChildren}
                size="sm"
                disabled={!globalReturnByDate || saving}
                className={!globalReturnByDate ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700 text-white'}
              >
                <Calendar className="w-4 h-4 mr-1" />
                {saving ? 'Applying...' : 'Apply to ALL'}
              </Button>
              <span className="text-xs text-purple-500">
                {!globalReturnByDate ? '(Set a date first)' : selectedChildren.size === 0 ? '(Use "Apply to ALL" for beginning of year)' : `(Selected: ${selectedChildren.size})`}
              </span>
            </div>
          </div>
        </div>

        {/* Search + Category Filter */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search children by name..."
              className="pl-10"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="category-filter-select"
          >
            <option value="All">All Categories</option>
            {[...new Set(children.map(c => c.category).filter(Boolean))].sort().map(cat => (
              <option key={cat} value={cat}>{cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
            ))}
          </select>
        </div>

        {/* Children List */}
        <div className="space-y-3">
          {/* Note about dropdown */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
            <ChevronDown className="w-5 h-5 text-blue-600" />
            <p className="text-sm text-blue-700 font-medium">
              Select Child's Records From Dropdown Arrow Below - Click dropdown arrow to expand child's forms
            </p>
          </div>
          
          {filteredChildren.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No children found</p>
            </div>
          ) : (
            filteredChildren.map(child => {
              const outstandingCount = getOutstandingForms(child.id).length;
              const isExpanded = expandedChild === child.id;
              const records = childRecords[child.id] || {};
              const isSelected = selectedChildren.has(child.id);
              
              return (
                <div
                  key={child.id}
                  className={`border rounded-lg overflow-hidden ${
                    outstandingCount > 0 ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'
                  } ${isSelected ? 'ring-2 ring-purple-400' : ''}`}
                >
                  {/* Child Header */}
                  <div
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-3">
                      {/* Selection checkbox for bulk actions */}
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleChildSelection(child.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-5 w-5"
                      />
                      <div 
                        className="flex items-center gap-3 flex-1"
                        onClick={() => handleExpandChild(child.id)}
                      >
                        <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold">
                          {child.firstName?.[0]}{child.lastName?.[0]}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">
                            {child.lastName}, {child.firstName}
                          </p>
                          <p className="text-sm text-gray-500">
                            {child.category?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div 
                      className="flex items-center gap-3"
                      onClick={() => handleExpandChild(child.id)}
                    >
                      {(() => {
                        const loaded = formDocuments[child.id];
                        const docCount = loaded ? Object.keys(loaded).length : (docCounts[child.id] || 0);
                        return docCount > 0 ? (
                          <span
                            data-testid={`doc-count-badge-${child.id}`}
                            title={`${docCount} completed form${docCount > 1 ? 's' : ''} on file`}
                            className="px-2.5 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium flex items-center gap-1"
                          >
                            <Paperclip className="w-3.5 h-3.5" />
                            {docCount}
                          </span>
                        ) : null;
                      })()}
                      {outstandingCount > 0 && (
                        <span className="px-3 py-1 bg-amber-200 text-amber-800 rounded-full text-sm font-medium flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4" />
                          {outstandingCount} Outstanding
                        </span>
                      )}
                      {isExpanded ? (
                        <ChevronUp className="w-6 h-6 text-teal-600 bg-teal-100 rounded-full p-1" />
                      ) : (
                        <ChevronDown className="w-6 h-6 text-teal-600 bg-teal-100 rounded-full p-1 animate-pulse" />
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t p-4 bg-white">
                      <div className="space-y-2">

                        {/* Instruction + Email Selected Forms bar — always visible */}
                        <div className="flex items-start justify-between gap-3 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                          <p className="text-xs text-gray-600 leading-relaxed">
                            <span className="font-semibold text-blue-800">Staff:</span> Use 📤 to upload a completed form manually or upload a blank form, tick the checkbox, and send it to the parent. Use 🔗 to send a secure upload link to a parent for that specific row. Multiple rows can also be sent — just click <strong>Email Selected Forms to Parent</strong>.
                          </p>
                          {(emailFormSelection[child.id]?.size || 0) > 0 && (
                            <div className="flex items-center gap-2 shrink-0">
                              <Button
                                size="sm"
                                className="bg-blue-600 text-white hover:bg-blue-700 h-7"
                                onClick={() => prepareEmailFilesToParent(child)}
                                data-testid={`email-selected-btn-${child.id}`}
                              >
                                ✉️ Email Selected Forms to Parent
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:bg-red-50 h-7 border border-red-200"
                                onClick={() => {
                                  if (window.confirm('Are you sure you want to clear all selected forms?')) {
                                    setEmailFormSelection(prev => { const n = {...prev}; delete n[child.id]; return n; });
                                  }
                                }}
                                data-testid={`clear-email-selection-${child.id}`}
                              >
                                Clear
                              </Button>
                            </div>
                          )}
                        </div>

                        {(() => {
                          const customDocs = Object.entries(formDocuments[child.id] || {})
                            .filter(([fid]) => fid.startsWith('custom_'));
                          if (customDocs.length === 0) return null;
                          return (
                            <div className="mb-3 border border-teal-100 rounded-lg overflow-hidden" data-testid={`custom-forms-section-${child.id}`}>
                              <div className="px-3 py-2 bg-teal-50 border-b border-teal-100">
                                <p className="text-xs font-semibold text-teal-800">Additional / Custom Forms</p>
                                <p className="text-[11px] text-teal-600">Returned center forms — not counted toward compliance.</p>
                              </div>
                              <div className="divide-y divide-gray-100">
                                {customDocs.map(([fid, doc]) => (
                                  <div key={fid} className="flex items-center justify-between px-3 py-2 bg-white" data-testid={`custom-form-row-${child.id}-${fid}`}>
                                    <span className="text-sm text-gray-800 truncate flex items-center gap-2">
                                      <span className="w-2 h-2 rounded-full bg-teal-400 flex-shrink-0" />
                                      {doc.customLabel || 'Additional Form'}
                                    </span>
                                    {(doc.data) && (
                                      <button
                                        className="text-teal-600 hover:underline text-xs ml-2 flex-shrink-0"
                                        onClick={() => viewFormDocument(child.id, fid)}
                                        data-testid={`custom-form-view-${child.id}-${fid}`}
                                      >
                                        View
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()}


                        {getFormsForChild(child.category).map(form => {
                          const formRecord = getEffectiveFormRecord(form.id, child.category, records[form.id] || {});
                          const isOverdue = isFormOverdue(formRecord);
                          const expDate = (form.renewalMonths === 0 || form.isOptional) ? null : (formRecord.expirationDate || calculateExpirationDate(formRecord.lastSubmitted, form.id, child.category, formRecord.expiryEnabled));
                          const isExpired = expDate && new Date(expDate + 'T00:00:00') < new Date();
                          const formDoc = formDocuments[child.id]?.[form.id];
                          const isUploading = uploadingForm?.childId === child.id && uploadingForm?.formId === form.id;
                          const isPartTimeOptional = !!form.isPartTimeOptionalExpiry;
                          
                          // Auto-outstanding: expired, manually marked, OR required yearly form with no date entered
                          const isEffectivelyOutstanding = !form.isOptional && (
                            formRecord.outstanding ||
                            isExpired ||
                            (form.renewalMonths > 0 && !formRecord.lastSubmitted)
                          );
                          
                          return (
                            <div
                              key={form.id}
                              className={`p-3 rounded-lg border group ${
                                form.isNotRequired
                                  ? 'bg-gray-50 border-gray-200'
                                  : isEffectivelyOutstanding
                                  ? 'bg-amber-50 border-amber-200'
                                  : formRecord.lastSubmitted 
                                    ? 'bg-green-50 border-green-200'
                                    : 'bg-gray-50 border-gray-200'
                              }`}
                              data-testid={`form-row-${form.id}`}
                            >
                              {/* Form Header Row */}
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  {/* Upload Button - Small square */}
                                  <div className="relative">
                                    {formDoc ? (
                                      <div className="flex items-center gap-1 flex-wrap">
                                        {formDoc.pendingVersion && (
                                          <>
                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-300" data-testid={`pending-review-badge-${form.id}`}>Awaiting Approval</span>
                                            <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] bg-indigo-50 hover:bg-indigo-100 text-indigo-700" onClick={() => viewFormDocument(child.id, form.id, { pending: true })} title="View pending submission" data-testid={`view-pending-${form.id}`}>View Pending</Button>
                                            {(form.id === 'health_report' || form.id === 'shot_records') && (
                                              <Input
                                                type="date"
                                                data-testid={`approve-date-input-${form.id}`}
                                                key={`approve-date-${child.id}-${form.id}-${formDoc.pendingVersion?.draftDateOnForm || 'empty'}`}
                                                defaultValue={formDoc.pendingVersion?.draftDateOnForm || ''}
                                                onBlur={(e) => {
                                                  const newValue = e.target.value;
                                                  if (newValue !== (formDoc.pendingVersion?.draftDateOnForm || '')) {
                                                    saveDraftDateOnForm(child.id, form.id, newValue);
                                                  }
                                                }}
                                                max={new Date().toISOString().slice(0, 10)}
                                                title="Date on Form (required to approve) — saved automatically as you type"
                                                className="h-7 w-[130px] text-[11px]"
                                              />
                                            )}
                                            <Button
                                              size="sm" variant="ghost"
                                              disabled={(form.id === 'health_report' || form.id === 'shot_records') && !formDoc.pendingVersion?.draftDateOnForm}
                                              className="h-7 px-2 text-[11px] bg-green-100 hover:bg-green-200 text-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                              onClick={() => approveReplacement(child.id, form.id, formDoc.pendingVersion?.draftDateOnForm)}
                                              title="Approve (archives previous current document)" data-testid={`approve-replacement-${form.id}`}>Approve</Button>
                                            <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] bg-amber-50 hover:bg-amber-100 text-amber-700" onClick={() => rejectReplacement(child.id, form.id)} title="Discard pending submission and reopen parent upload" data-testid={`reject-replacement-${form.id}`}>Request Again</Button>
                                          </>
                                        )}
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-7 w-7 p-0 bg-green-100 hover:bg-green-200 text-green-700"
                                          onClick={() => viewFormDocument(child.id, form.id)}
                                          title="View uploaded document"
                                        >
                                          <Eye className="w-3.5 h-3.5" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-7 w-7 p-0 bg-blue-100 hover:bg-blue-200 text-blue-700"
                                          onClick={() => downloadFormDocument(formDoc, form.id, child.name || `${child.lastName}_${child.firstName}`)}
                                          title="Download"
                                        >
                                          <Download className="w-3.5 h-3.5" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-7 w-7 p-0 bg-indigo-50 hover:bg-indigo-100 text-indigo-600"
                                          onClick={() => openPageUploadModal(child.id, form.id, form.name, formDoc)}
                                          title="Add pages to this document"
                                          data-testid={`add-pages-${form.id}`}
                                        >
                                          <FileStack className="w-3.5 h-3.5" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-7 w-7 p-0 bg-red-50 hover:bg-red-100 text-red-500"
                                          onClick={() => deleteFormDocument(child.id, form.id)}
                                          title="Delete document"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-1">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className={`h-7 w-7 p-0 ${isUploading ? 'opacity-50' : 'hover:bg-sky-50 hover:border-sky-300'}`}
                                          onClick={() => openPageUploadModal(child.id, form.id, form.name, null)}
                                          disabled={isUploading}
                                          title="Staff Upload"
                                          data-testid={`staff-upload-${form.id}`}
                                        >
                                          {isUploading ? (
                                            <div className="w-3.5 h-3.5 border-2 border-sky-400 border-t-transparent rounded-full animate-spin" />
                                          ) : (
                                            <Upload className="w-3.5 h-3.5 text-gray-500" />
                                          )}
                                        </Button>
                                        {(() => {
                                          const parentHasEmail = !!child.pickupPersons?.find(p => p.email)?.email;
                                          const isSending = sendingFormLink?.childId === child.id && sendingFormLink?.formId === form.id;
                                          return (
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className={`h-7 w-7 p-0 ${isSending ? 'opacity-50' : !parentHasEmail ? 'opacity-40 cursor-not-allowed' : 'hover:bg-amber-50 hover:border-amber-300'}`}
                                              onClick={() => parentHasEmail && prepareFormLinkEmail(child.id, form.id, child.name)}
                                              disabled={isSending || !parentHasEmail}
                                              title={parentHasEmail ? "Send upload link to parent" : "No parent email on file"}
                                              data-testid={`send-form-link-${child.id}-${form.id}`}
                                            >
                                              {isSending ? (
                                                <div className="w-3.5 h-3.5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                                              ) : (
                                                <Link className={`w-3.5 h-3.5 ${parentHasEmail ? 'text-amber-600' : 'text-gray-300'}`} />
                                              )}
                                            </Button>
                                          );
                                        })()}
                                      </div>
                                    )}
                                  </div>
                                  
                                  {(() => {
                                    const hasFile = !!formDoc;
                                    const parentEmail = getParentEmailForChild(child);
                                    const selectable = hasFile && !!parentEmail;
                                    const isChecked = !!(emailFormSelection[child.id]?.has?.(form.id));
                                    const tip = !hasFile
                                      ? "Upload a file first to email it to the parent"
                                      : !parentEmail
                                        ? "No parent email on file"
                                        : "Select to email this form to parent";
                                    return (
                                      <Checkbox
                                        id={`emailsel-${child.id}-${form.id}`}
                                        checked={isChecked}
                                        disabled={!selectable}
                                        onCheckedChange={(checked) => toggleEmailFormSelection(child.id, form.id, !!checked)}
                                        title={tip}
                                        data-testid={`email-select-${child.id}-${form.id}`}
                                        className={selectable ? "data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" : "opacity-40 cursor-not-allowed"}
                                      />
                                    );
                                  })()}
                                  <label
                                    htmlFor={`emailsel-${child.id}-${form.id}`}
                                    className="text-sm font-medium cursor-pointer"
                                    title="Select to email this form to parent"
                                  >
                                    {form.name}
                                    {form.isOptional
                                      ? <span className="text-xs bg-gray-100 text-gray-500 border border-gray-200 px-1.5 py-0.5 rounded ml-2 font-normal">Optional — Info Only</span>
                                      : <span className="text-xs text-blue-600 ml-2">({form.renewalLabel})</span>}
                                  </label>
                                  {form.isOptional && (
                                    <span className="text-xs text-gray-400 italic block">Not tracked in compliance score. Informational use only.</span>
                                  )}
                                  {formDoc && (
                                    <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                                      <Paperclip className="w-3 h-3" />
                                      Uploaded
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  {form.isNotRequired ? (
                                    <span className="text-xs text-gray-400 font-medium">Not Required</span>
                                  ) : formRecord.notApplicable || formRecord.lastSubmitted === 'N/A' ? (
                                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded font-medium">N/A</span>
                                  ) : !formRecord.lastSubmitted ? (
                                    <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded font-medium">Outstanding</span>
                                  ) : isExpired ? (
                                    <span className="text-xs bg-red-200 text-red-800 px-2 py-0.5 rounded font-medium">Expired</span>
                                  ) : !expDate ? (
                                    form.isSchoolAgeOnFile
                                      ? <span className="text-xs text-green-600 font-medium">On File — School Age</span>
                                      : <span className="text-xs text-green-600 font-medium">On File</span>
                                  ) : formRecord.outstanding ? (
                                    <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded font-medium">Outstanding</span>
                                  ) : (
                                    <span className="text-xs text-green-600 font-medium">✓ Current</span>
                                  )}
                                </div>
                              </div>
                              
                              {/* Form Details Row - Always visible */}
                              {form.isNotRequired ? (
                                <p className="text-xs text-gray-400 ml-8 mt-1">Not required for this child's enrollment type.</p>
                              ) : (
                              <div className="flex flex-wrap items-center gap-4 ml-8 text-sm">
                                {/* 1. Date on Form (renamed from Last Submitted) */}
                                <div className="flex items-center gap-2">
                                  <Label className="text-xs text-gray-500 whitespace-nowrap">Date on Form:</Label>
                                  <Input
                                    type="date"
                                    key={`dof-${child.id}-${form.id}-${formRecord.lastSubmitted || 'empty'}`}
                                    defaultValue={formRecord.lastSubmitted || ''}
                                    onBlur={(e) => {
                                      const newValue = e.target.value;
                                      if (newValue !== (formRecord.lastSubmitted || '')) {
                                        updateLastSubmittedDate(child.id, form.id, newValue);
                                      }
                                    }}
                                    className="w-36 h-7 text-xs"
                                    title="Date shown on the form"
                                    data-testid={`date-on-form-${form.id}`}
                                  />
                                  {formRecord.lastSubmitted && (
                                    <button
                                      className="text-xs text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                                      onClick={() => clearFormDates(child.id, form.id)}
                                      title="Clear all dates for this form"
                                      data-testid={`clear-dates-${form.id}`}
                                    >
                                      Clear
                                    </button>
                                  )}
                                </div>
                                
                                {/* 2. Expiration Date - Auto-calculated (read-only), or "On File" for no-expiry forms */}
                                <div className="flex items-center gap-2">
                                  <Label className="text-xs text-gray-500 whitespace-nowrap">
                                    {form.renewalMonths === 0
                                      ? (formRecord.lastSubmitted ? 'Status:' : 'Expires:')
                                      : (!formRecord.lastSubmitted ? 'Status:' : 'Expires:')}
                                  </Label>
                                  {form.renewalMonths === 0 ? (
                                    <span className={`text-xs font-medium px-2 py-1 rounded ${
                                      formRecord.lastSubmitted ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                                    }`}>
                                      {formRecord.lastSubmitted ? 'On File ✓' : 'N/A'}
                                    </span>
                                  ) : (
                                    <span className={`text-xs font-medium px-2 py-1 rounded ${
                                      isExpired ? 'bg-red-100 text-red-700' : expDate ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                                    }`}>
                                      {!formRecord.lastSubmitted ? 'Not On File' : expDate ? formatDate(expDate) : 'N/A'}
                                      {isExpired && ' (EXPIRED)'}
                                    </span>
                                  )}
                                </div>
                                
                                {/* 3. Return By - Always visible, editable */}
                                <div className="flex items-center gap-2">
                                  <Label className="text-xs text-gray-500 whitespace-nowrap">Return By:</Label>
                                  <Input
                                    type="date"
                                    defaultValue={formRecord.returnBy || ''}
                                    onBlur={(e) => {
                                      const newValue = e.target.value;
                                      if (newValue !== (formRecord.returnBy || '')) {
                                        updateReturnByDate(child.id, form.id, newValue);
                                      }
                                    }}
                                    className={`w-36 h-7 text-xs ${isOverdue ? 'border-red-400 bg-red-50' : ''}`}
                                    title="Date center wants form returned"
                                    data-testid={`return-by-${form.id}`}
                                  />
                                  {formRecord.returnBy && isOverdue && (
                                    <span className="text-xs text-red-600 font-medium">(OVERDUE)</span>
                                  )}
                                </div>
                                
                                {/* Mark Returned Button - Show when outstanding OR expired */}
                                {isEffectivelyOutstanding && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs border-green-400 text-green-700 hover:bg-green-50"
                                    onClick={() => markRecordReturned(child.id, form.id)}
                                    data-testid={`mark-returned-${form.id}`}
                                  >
                                    <CheckSquare className="w-3 h-3 mr-1" />
                                    Mark Returned
                                  </Button>
                                )}
                              </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Action Buttons */}
                      {outstandingCount > 0 && (
                        <div className="mt-4 pt-4 border-t flex flex-wrap gap-2">
                          <Button
                            onClick={() => generatePDF(child.id)}
                            size="sm"
                            className="bg-red-600 hover:bg-red-700"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download PDF
                          </Button>
                          <Button
                            onClick={() => printRecord(child.id)}
                            size="sm"
                            variant="outline"
                            className="border-gray-300"
                          >
                            <Printer className="w-4 h-4 mr-1" />
                            Print
                          </Button>
                          <Button
                            onClick={() => prepareIndividualEmail(child.id)}
                            size="sm"
                            variant="outline"
                            className="border-blue-300 text-blue-700 hover:bg-blue-50"
                            data-testid={`email-parent-btn-${child.id}`}
                          >
                            <Mail className="w-4 h-4 mr-1" />
                            Email to Parent
                          </Button>
                          <Button
                            onClick={() => prepareIndividualSms(child.id)}
                            size="sm"
                            variant="outline"
                            className="border-teal-300 text-teal-700 hover:bg-teal-50"
                            data-testid={`sms-parent-btn-${child.id}`}
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            SMS Parent
                          </Button>
                        </div>
                      )}
                      
                      {/* Child Service Report - Only for Full-Time Children (not school-age) */}
                      {isFullTimeChild(child) && (() => {
                        const latestReport = serviceReportLookup[child.id];
                        const completedDate = latestReport?.facilityReporterDate;
                        let expirationDate = null;
                        let isExpired = false;
                        let isExpiringSoon = false;
                        if (completedDate) {
                          const d = new Date(completedDate + 'T00:00:00');
                          expirationDate = new Date(d);
                          expirationDate.setFullYear(expirationDate.getFullYear() + 1);
                          const now = new Date();
                          isExpired = expirationDate < now;
                          const thirtyDaysOut = new Date(now);
                          thirtyDaysOut.setDate(thirtyDaysOut.getDate() + 30);
                          isExpiringSoon = !isExpired && expirationDate < thirtyDaysOut;
                        }
                        const statusColor = !completedDate ? 'orange' : isExpired ? 'red' : isExpiringSoon ? 'yellow' : 'green';
                        const bgClass = statusColor === 'green' ? 'bg-green-50 border-green-200' : statusColor === 'yellow' ? 'bg-yellow-50 border-yellow-200' : statusColor === 'red' ? 'bg-red-50 border-red-200' : 'bg-teal-50 border-teal-200';
                        return (
                        <div className="mt-4 pt-4 border-t">
                          <div className={`${bgClass} border rounded-lg p-3`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <BookOpen className={`w-5 h-5 ${statusColor === 'green' ? 'text-green-600' : statusColor === 'red' ? 'text-red-600' : 'text-teal-600'}`} />
                                <div>
                                  <p className="font-medium text-gray-800 text-sm">Child Service Report</p>
                                  {completedDate ? (
                                    <div className="text-xs">
                                      <span className={statusColor === 'green' ? 'text-green-600' : statusColor === 'yellow' ? 'text-yellow-600' : 'text-red-600'}>
                                        Completed: {new Date(completedDate + 'T00:00:00').toLocaleDateString('en-US', {month:'2-digit', day:'2-digit', year:'numeric'})}
                                        {' | '}
                                        Expires: {expirationDate.toLocaleDateString('en-US', {month:'2-digit', day:'2-digit', year:'numeric'})}
                                        {isExpired && ' (EXPIRED)'}
                                        {isExpiringSoon && ' (Expiring Soon)'}
                                      </span>
                                    </div>
                                  ) : (
                                    <p className="text-xs text-orange-600">No report on file</p>
                                  )}
                                </div>
                              </div>
                              <Button
                                onClick={() => openServiceReportDialog(child)}
                                size="sm"
                                className="bg-teal-600 hover:bg-teal-700"
                                data-testid={`child-service-report-btn-${child.id}`}
                              >
                                <FileText className="w-4 h-4 mr-1" />
                                Open Report
                              </Button>
                            </div>
                          </div>
                        </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

      {/* Email Preview Dialog */}
      <AlertDialog open={showEmailPreview} onOpenChange={setShowEmailPreview}>
        <AlertDialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-purple-800">
              <Eye className="w-5 h-5" />
              Email Preview - Review Before Sending
            </AlertDialogTitle>
            <AlertDialogDescription>
              Review the list below. Remove any children you don't want to email by clicking the X button.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="flex-1 overflow-y-auto my-4 pr-2">
            {emailPreviewData.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No children to email</p>
            ) : (
              <div className="space-y-3">
                {emailPreviewData.map((item, index) => (
                  <div 
                    key={item.childId} 
                    className={`p-3 rounded-lg border ${item.hasEmail ? 'bg-white border-gray-200' : 'bg-red-50 border-red-200'}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-800">{index + 1}. {item.childName}</span>
                          {!item.hasEmail && (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">No email on file</span>
                          )}
                        </div>
                        {item.hasEmail && (
                          <p className="text-sm text-gray-600 mb-2">
                            <Mail className="w-3 h-3 inline mr-1" />
                            {item.parentEmail}
                          </p>
                        )}
                        <div className="text-xs text-gray-500">
                          <span className="font-medium">Outstanding forms ({item.outstandingForms.length}):</span>
                          <ul className="mt-1 ml-4 list-disc">
                            {item.outstandingForms.slice(0, 3).map((form, i) => (
                              <li key={i}>
                                {form.name} 
                                <span className="text-purple-600 ml-1">({form.renewalLabel})</span>
                                {form.returnBy && (
                                  <span className="text-amber-600 ml-1">- Return by: {form.returnBy}</span>
                                )}
                              </li>
                            ))}
                            {item.outstandingForms.length > 3 && (
                              <li className="text-gray-400">...and {item.outstandingForms.length - 3} more</li>
                            )}
                          </ul>
                        </div>
                      </div>
                      <Button
                        onClick={() => removeFromEmailPreview(item.childId)}
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:bg-red-50 flex-shrink-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t pt-3 bg-gray-50 -mx-6 px-6 -mb-6 pb-4 rounded-b-lg">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <span className="font-medium">{emailPreviewData.filter(i => i.hasEmail).length}</span> emails will be sent
                {emailPreviewData.filter(i => !i.hasEmail).length > 0 && (
                  <span className="text-red-500 ml-2">
                    ({emailPreviewData.filter(i => !i.hasEmail).length} skipped - no email)
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={confirmAndSendBulkEmails}
                  disabled={emailPreviewData.filter(i => i.hasEmail).length === 0}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Mail className="w-4 h-4 mr-1" />
                  Send {emailPreviewData.filter(i => i.hasEmail).length} Email(s)
                </AlertDialogAction>
              </div>
            </div>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Individual Email Confirmation Dialog */}
      <AlertDialog open={showIndividualEmailConfirm} onOpenChange={setShowIndividualEmailConfirm}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-blue-800">
              <Mail className="w-5 h-5" />
              Confirm Email - Review Before Sending
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-left">
                {individualEmailData && (
                  <>
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <p className="font-semibold text-blue-800 mb-1">Sending to:</p>
                      <p className="text-blue-700">{individualEmailData.childName}'s Parent</p>
                      <p className="text-sm text-blue-600">{individualEmailData.parentEmail}</p>
                    </div>
                    
                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <p className="font-semibold text-gray-700 mb-2">Outstanding Forms ({individualEmailData.outstandingForms.length}):</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {individualEmailData.outstandingForms.map((form, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-amber-500">•</span>
                            <span>
                              {form.name}
                              <span className="text-purple-600 text-xs ml-1">({form.renewalLabel})</span>
                              {form.returnBy && (
                                <span className="text-amber-600 text-xs ml-1">- Return by: {form.returnBy}</span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-gray-700">Add a note to the parent <span className="text-gray-400 font-normal">(optional)</span></p>
                      <textarea
                        value={individualEmailNote}
                        onChange={e => setIndividualEmailNote(e.target.value)}
                        placeholder="e.g. Your child's emergency contact form has expired and must be returned by Friday to remain enrolled."
                        rows={3}
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                        data-testid="individual-email-note"
                      />
                      <p className="text-xs text-gray-400">This note will appear in the email above the forms list.</p>
                    </div>

                    <p className="text-sm text-gray-500 italic">
                      Are you sure you want to send this email?
                    </p>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="border-gray-300">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmAndSendIndividualEmail}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Mail className="w-4 h-4 mr-1" />
              Yes, Send Email
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Individual SMS Confirmation Dialog */}
      <AlertDialog open={showIndividualSmsConfirm} onOpenChange={setShowIndividualSmsConfirm}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-teal-800">
              <MessageSquare className="w-5 h-5" />
              Confirm SMS - Review Before Sending
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-left">
                {individualSmsData && (() => {
                  const smsBody = buildRecordsSmsBody(individualSmsData.childName, individualSmsData.outstandingCount, individualSmsNote);
                  const charCount = smsBody.length;
                  const segments = charCount <= 160 ? 1 : Math.ceil(charCount / 153);
                  const overLimit = charCount > 300;
                  return (
                    <>
                      <div className="bg-teal-50 p-3 rounded-lg border border-teal-200">
                        <p className="font-semibold text-teal-800 mb-1">Sending to:</p>
                        <p className="text-teal-700">{individualSmsData.childName}'s Parent</p>
                        <p className="text-sm text-teal-600" data-testid="sms-recipient-phone">{maskPhoneNumber(individualSmsData.parentPhone)}</p>
                      </div>

                      <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                        <p className="font-semibold text-gray-700">
                          Outstanding Forms: <span className="font-normal">{individualSmsData.outstandingCount}</span>
                        </p>
                      </div>

                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-gray-700">Add a note to the parent <span className="text-gray-400 font-normal">(optional)</span></p>
                        <textarea
                          value={individualSmsNote}
                          onChange={e => setIndividualSmsNote(e.target.value)}
                          placeholder="e.g. Please drop off the updated form by Friday."
                          rows={2}
                          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-400 resize-none"
                          data-testid="individual-sms-note"
                        />
                      </div>

                      <div className="bg-white border border-gray-200 rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">Message preview:</p>
                        <p className="text-sm text-gray-700" data-testid="sms-body-preview">{smsBody}</p>
                        <p className={`text-xs mt-2 ${overLimit ? 'text-red-600 font-semibold' : 'text-gray-400'}`} data-testid="sms-char-count">
                          {charCount}/300 characters{segments > 1 && !overLimit && ` — will send as ${segments} SMS segments`}
                          {overLimit && ' — shorten your note to send'}
                        </p>
                      </div>

                      <p className="text-sm text-gray-500 italic">
                        Are you sure you want to send this text message?
                      </p>
                    </>
                  );
                })()}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="border-gray-300" data-testid="sms-cancel-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmAndSendIndividualSms}
              disabled={sendingSms || (individualSmsData && buildRecordsSmsBody(individualSmsData.childName, individualSmsData.outstandingCount, individualSmsNote).length > 300)}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="sms-confirm-send-btn"
            >
              <MessageSquare className="w-4 h-4 mr-1" />
              {sendingSms ? 'Sending...' : 'Yes, Send SMS'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Form Upload Link Email Confirmation Dialog */}
      <AlertDialog open={showFormLinkConfirm} onOpenChange={setShowFormLinkConfirm}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
              <Mail className="w-5 h-5" />
              Send Upload Link to Parent?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-left">
                {formLinkEmailData && (
                  <>
                    <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                      <p className="font-semibold text-amber-800 mb-2">Email Details:</p>
                      <div className="space-y-1 text-sm">
                        <p><span className="text-gray-600">Child:</span> <span className="text-amber-700 font-medium">{formLinkEmailData.childName}</span></p>
                        <p><span className="text-gray-600">Form:</span> <span className="text-amber-700 font-medium">{formLinkEmailData.formName}</span></p>
                        <p><span className="text-gray-600">Parent Email:</span> <span className="text-amber-700 font-medium">{formLinkEmailData.parentEmail}</span></p>
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-600">
                      The parent will receive an email with a secure link to upload the <strong>{formLinkEmailData.formName}</strong> document.
                    </p>
                    
                    <p className="text-sm text-gray-500 italic">
                      Are you ready to send this email?
                    </p>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="border-gray-300">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmAndSendFormLink}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Mail className="w-4 h-4 mr-1" />
              Yes, Send Email
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Child Service Report Dialog */}
      <ChildServiceReportDialog
        open={serviceReportDialogOpen}
        onOpenChange={setServiceReportDialogOpen}
        childId={serviceReportChild?.id}
        childName={serviceReportChild?.name}
        childBirthdate={serviceReportChild?.birthdate}
        childPickupPersons={serviceReportChild?.pickupPersons}
        onSaved={() => {
          fetchServiceReportStatuses();
        }}
      />
      {/* Multi-Page Document Uploader — Take Photo / Choose from Photos or Files,
          compose numbered pages into one PDF, or Add Pages to an existing document */}
      <MultiPageDocumentUploader
        open={!!pageUploadTarget}
        onOpenChange={(open) => { if (!open) setPageUploadTarget(null); }}
        formName={pageUploadTarget?.formName}
        existingDoc={pageUploadTarget?.existingDoc}
        requiresDateOnForm={pageUploadTarget?.formId === 'health_report' || pageUploadTarget?.formId === 'shot_records'}
        promptDateOnForm={() => promptDateOnFormIfRequired(pageUploadTarget?.formId)}
        onUpload={async (file, dateOnForm) => {
          const ok = await uploadFormDocumentFile(pageUploadTarget.childId, pageUploadTarget.formId, file, dateOnForm);
          if (ok) setPageUploadTarget(null);
          return ok;
        }}
      />



      {/* Email Selected Files to Parent — confirm */}
      <AlertDialog open={showEmailFilesConfirm} onOpenChange={setShowEmailFilesConfirm}>
        <AlertDialogContent data-testid="email-files-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-blue-700 flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Email Selected Forms to Parent?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-left">
                {emailFilesData && (
                  <>
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <p className="font-semibold text-blue-800 mb-2">Email Details:</p>
                      <div className="space-y-1 text-sm">
                        <p><span className="text-gray-600">Child:</span> <span className="text-blue-700 font-medium">{emailFilesData.childName}</span></p>
                        <p><span className="text-gray-600">Parent Email:</span> <span className="text-blue-700 font-medium">{emailFilesData.parentEmail}</span></p>
                        <p><span className="text-gray-600">Forms ({emailFilesData.formIds.length}):</span></p>
                        <ul className="ml-4 list-disc text-blue-700">
                          {emailFilesData.formNames.map((n, i) => (
                            <li key={i}>{n}</li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email-files-note" className="text-sm text-gray-700">
                        Note to Parent (optional)
                      </Label>
                      <textarea
                        id="email-files-note"
                        rows={3}
                        value={emailFilesNote}
                        onChange={(e) => setEmailFilesNote(e.target.value)}
                        placeholder="e.g., Please print, fill out the highlighted fields, sign, and return at drop-off Monday."
                        className="mt-1 w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="email-files-note-input"
                      />
                    </div>

                    <p className="text-xs text-gray-500 italic">
                      The parent will receive one email with all selected files attached, plus a secure link for each form so they can upload completed copies back into this child's record.
                    </p>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="border-gray-300" disabled={sendingEmailFiles}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmAndEmailFilesToParent}
              disabled={sendingEmailFiles}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="email-files-confirm-send"
            >
              <Mail className="w-4 h-4 mr-1" />
              {sendingEmailFiles ? "Sending..." : "Yes, Send Email"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
          </TabsContent>
          
          {enrollmentPermitted && (
          <TabsContent value="enrollment">
            <div className="space-y-6">

              {/* Pending Enrollment workflow (relocated from Records Management) */}
              <PendingEnrollmentsPanel onAccepted={fetchChildren} />

              {/* Template Library */}
              <div className="bg-white rounded-lg border-2 border-teal-200 overflow-hidden">
                <div className="bg-gradient-to-r from-teal-500 to-cyan-500 px-5 py-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <ClipboardList className="w-5 h-5" />
                      Blank Form Templates
                    </h3>
                    <p className="text-teal-100 text-sm mt-1">
                      Upload one blank copy of each form. Parents will download, complete, and return them.
                    </p>
                  </div>
                  <Button size="sm" variant="outline"
                    className="bg-white/20 border-white/40 text-white hover:bg-white/30 text-xs flex-shrink-0"
                    onClick={printFullPacket}
                    data-testid="print-full-packet-btn">
                    <Printer className="w-3.5 h-3.5 mr-1" />Print Full Packet
                  </Button>
                </div>

                <div className="divide-y divide-gray-100">
                  {[
                    { key: 'shot_records',         label: 'Child Shot Records' },
                    { key: 'enrollment_info',       label: 'Enrollment Information Sheet' },
                    { key: 'financial_contract',    label: 'Financial & Contract Agreement' },
                    { key: 'about_child',           label: 'All About Your Child Form' },
                    { key: 'health_report',         label: 'Child Health Report' },
                    { key: 'emergency_consent',     label: 'Emergency Contact / Parental Consent Form' },
                    { key: 'transportation_policy', label: 'School Age Transportation & Transfer of Care Policy', tooltip: 'This form will only be sent to School Age children when uploaded.' },
                  ].map(({ key, label, tooltip }) => {
                    const template = enrollmentTemplates.find(t => t.documentType === key);
                    const isUploading = uploadingTemplate === key;
                    return (
                      <div key={key} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <input type="checkbox"
                            checked={selectedPacketForms.has(key)}
                            onChange={() => togglePacketForm(key)}
                            className="w-3 h-3 accent-teal-300 cursor-pointer flex-shrink-0 opacity-75"
                            data-testid={`packet-form-checkbox-${key}`} />
                          <FileText className="w-4 h-4 text-teal-500 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-800 truncate">{label}</span>
                          {PDF_FIELD_CATALOG[key] && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      downloadStarterTemplate(key);
                                    }}
                                    className="flex-shrink-0"
                                    data-testid={`download-starter-${key}`}
                                    aria-label={`Download starter PDF for ${label}`}
                                  >
                                    <Download className="w-3.5 h-3.5 text-emerald-600 hover:text-emerald-800 cursor-pointer" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-[240px] text-xs leading-snug">
                                  <div className="font-semibold mb-1">Reference</div>
                                  <div className="text-gray-600">Download this form as a reference — shows where fields should be placed for auto-sync.</div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          {PDF_FIELD_CATALOG[key] && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPdfGuideInitialTab(key);
                                      setPdfGuideOpen(true);
                                    }}
                                    className="flex-shrink-0"
                                    data-testid={`guide-icon-${key}`}
                                    aria-label={`PDF field names for ${label}`}
                                  >
                                    <Info className="w-3.5 h-3.5 text-blue-500 hover:text-blue-700 cursor-pointer" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-[260px] text-xs leading-snug">
                                  <div className="font-semibold mb-1">Reference guide</div>
                                  <div className="text-gray-600">Reference guide: see exactly how to name your Adobe fields to ensure they link to child records.</div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          {tooltip && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="w-3.5 h-3.5 text-teal-400 flex-shrink-0 cursor-pointer" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-[220px] text-center text-xs leading-snug">
                                  {tooltip}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>

                        <div className="flex items-center gap-2 ml-4">
                          {template ? (
                            <>
                              <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                                <CheckCircle className="w-3.5 h-3.5" /> Template Uploaded
                              </span>
                              <Button size="sm" variant="ghost"
                                className="h-7 w-7 p-0 bg-green-100 hover:bg-green-200 text-green-700"
                                onClick={() => viewEnrollmentTemplate(key)}
                                title="View template">
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost"
                                className="h-7 w-7 p-0 bg-red-50 hover:bg-red-100 text-red-500"
                                onClick={() => deleteEnrollmentTemplate(key)}
                                title="Remove template">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs text-gray-400">No Template</span>
                          )}

                          {/* Upload input */}
                          <input
                            type="file"
                            id={`enroll-tmpl-${key}`}
                            className="hidden"
                            accept="application/pdf,image/jpeg,image/png"
                            onChange={(e) => handleTemplateUpload(e, key)}
                          />
                          <Button size="sm" variant="outline"
                            className={`h-7 w-7 p-0 ${isUploading ? 'opacity-50' : 'hover:bg-sky-50 hover:border-sky-300'}`}
                            onClick={() => document.getElementById(`enroll-tmpl-${key}`).click()}
                            disabled={isUploading}
                            title={template ? 'Replace template' : 'Upload template'}>
                            {isUploading
                              ? <div className="w-3.5 h-3.5 border-2 border-sky-400 border-t-transparent rounded-full animate-spin" />
                              : <Upload className="w-3.5 h-3.5 text-gray-500" />}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Additional Center Forms */}
              <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden">
                <div className="bg-gray-50 border-b border-gray-200 px-5 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-gray-700 font-semibold flex items-center gap-2">
                        <Paperclip className="w-5 h-5 text-gray-500" />
                        Additional Center Forms
                      </h3>
                      <p className="text-gray-500 text-xs mt-1">
                        These forms are included in the parent enrollment packet. They do not count toward your state compliance score.
                      </p>
                    </div>
                    <Button size="sm" variant="outline"
                      className="h-8 text-xs gap-1 border-gray-300 text-gray-600 hover:bg-gray-100 flex-shrink-0 ml-4"
                      onClick={() => { setAddingCustomForm(!addingCustomForm); setNewCustomLabel(''); }}
                      data-testid="add-custom-form-btn">
                      {addingCustomForm
                        ? <><X className="w-3.5 h-3.5" /> Done</>
                        : <><Upload className="w-3.5 h-3.5" /> Add Form</>}
                    </Button>
                  </div>
                </div>

                {addingCustomForm && (
                  <div className="px-5 py-4 bg-gray-50 border-b border-gray-100 space-y-3">
                    <div>
                      <Label className="text-xs font-medium text-gray-600 mb-1 block">Form Name</Label>
                      <Input
                        placeholder="e.g. Parent Handbook, Field Trip Policy..."
                        value={newCustomLabel}
                        onChange={e => setNewCustomLabel(e.target.value)}
                        className="h-8 text-sm"
                        data-testid="custom-form-label-input"
                      />
                    </div>
                    <div className="flex items-center gap-6">
                      <span className="text-xs font-medium text-gray-600">Type:</span>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" checked={newCustomReturnRequired}
                          onChange={() => setNewCustomReturnRequired(true)}
                          className="accent-teal-600" />
                        <span className="text-xs text-gray-700">
                          Return Required <span className="text-gray-400">(parent uploads signed copy)</span>
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" checked={!newCustomReturnRequired}
                          onChange={() => setNewCustomReturnRequired(false)}
                          className="accent-teal-600" />
                        <span className="text-xs text-gray-700">
                          Acknowledge Only <span className="text-gray-400">(parent marks as read)</span>
                        </span>
                      </label>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="file"
                        id="custom-form-file-input"
                        className="hidden"
                        accept="application/pdf,image/jpeg,image/png"
                        onChange={e => setSelectedCustomFile(e.target.files?.[0] || null)}
                      />
                      <Button size="sm" variant="outline"
                        className="h-8 text-xs gap-1 border-gray-300 text-gray-600 hover:bg-gray-100"
                        onClick={() => document.getElementById('custom-form-file-input').click()}
                        data-testid="custom-form-choose-btn">
                        <Paperclip className="w-3.5 h-3.5" />
                        {selectedCustomFile ? 'Change File' : 'Choose File'}
                      </Button>
                      {selectedCustomFile && (
                        <span className="text-xs text-gray-600 truncate max-w-xs">{selectedCustomFile.name}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 pt-1 border-t border-gray-200">
                      <Button size="sm"
                        className="h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white gap-1"
                        onClick={handleCustomFormUpload}
                        disabled={uploadingCustom || !newCustomLabel.trim() || !selectedCustomFile}
                        data-testid="custom-form-save-btn">
                        {uploadingCustom
                          ? <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>
                          : <><Save className="w-3.5 h-3.5" /> Save Form</>}
                      </Button>
                      <span className="text-xs text-gray-400">PDF, JPG, or PNG · Form stays open to add more</span>
                    </div>
                  </div>
                )}

                {enrollmentTemplates.filter(t => t.isCustom).length === 0 ? (
                  <div className="px-5 py-6 text-center">
                    <p className="text-sm text-gray-400">No additional forms added yet.</p>
                    <p className="text-xs text-gray-300 mt-1">Click "Add Form" to include center-specific forms in all enrollment packets.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {enrollmentTemplates.filter(t => t.isCustom).map(tmpl => (
                      <div key={tmpl.customId} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <span className="text-sm font-medium text-gray-800 truncate block">{tmpl.customLabel}</span>
                            <span className={`text-xs font-medium ${tmpl.returnRequired ? 'text-blue-500' : 'text-amber-500'}`}>
                              {tmpl.returnRequired ? 'Return Required' : 'Acknowledge Only'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Button size="sm" variant="ghost"
                            className="h-7 w-7 p-0 bg-green-100 hover:bg-green-200 text-green-700"
                            onClick={() => viewEnrollmentTemplate(null, tmpl.customId)}
                            title="View form">
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost"
                            className="h-7 w-7 p-0 bg-red-50 hover:bg-red-100 text-red-500"
                            onClick={() => deleteCustomTemplate(tmpl.customId)}
                            title="Remove form">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Child List + Send Buttons */}
              <div className="bg-white rounded-lg border-2 border-teal-200 overflow-hidden">
                <div className="bg-gradient-to-r from-teal-500 to-cyan-500 px-5 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-white font-semibold flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        Send Enrollment Packets
                      </h3>
                      <p className="text-teal-100 text-sm mt-1">
                        Click Send to email the parent a secure link to download and return their child's forms.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={packetCategoryFilter}
                        onChange={e => setPacketCategoryFilter(e.target.value)}
                        className="text-xs rounded-md px-2 py-1.5 bg-white/20 border border-white/40 text-white focus:outline-none cursor-pointer"
                        data-testid="packet-category-filter">
                        <option value="All" className="text-gray-800 bg-white">All Categories</option>
                        {[...new Set(children.map(c => c.category).filter(Boolean))].sort().map(cat => (
                          <option key={cat} value={cat} className="text-gray-800 bg-white">
                            {cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </option>
                        ))}
                      </select>
                      <Button size="sm" variant="outline"
                        className="bg-white/20 border-white/40 text-white hover:bg-white/30 text-xs flex items-center gap-1"
                        onClick={printAllEnrollmentSummary}
                        disabled={children.filter(c => !!packetStatuses[c.id]).length === 0}
                        data-testid="print-all-enrollment-summary"
                        title="Print compliance summary for all children with sent packets">
                        <Printer className="w-3.5 h-3.5" />
                        Print All
                      </Button>
                      <Button size="sm" variant="outline"
                        className="bg-white/20 border-white/40 text-white hover:bg-white/30 text-xs"
                        onClick={selectAllNotSent}
                        data-testid="select-all-not-sent">
                        {children
                          .filter(c => packetCategoryFilter === 'All' || (c.category || '').toLowerCase() === packetCategoryFilter.toLowerCase())
                          .filter(c => !packetStatuses[c.id])
                          .every(c => packetSelectedChildren.has(c.id)) &&
                         children.filter(c => packetCategoryFilter === 'All' || (c.category || '').toLowerCase() === packetCategoryFilter.toLowerCase()).some(c => !packetStatuses[c.id])
                          ? 'Deselect All' : 'Select All (Not Sent)'}
                      </Button>
                      <Button size="sm" variant="outline"
                        className="bg-white/20 border-white/40 text-white hover:bg-white/30 text-xs"
                        onClick={selectAllChildren}
                        data-testid="select-all-everyone">
                        {(() => {
                          const allIds = children
                            .filter(c => packetCategoryFilter === 'All' || (c.category || '').toLowerCase() === packetCategoryFilter.toLowerCase())
                            .map(c => c.id);
                          return allIds.length > 0 && allIds.every(id => packetSelectedChildren.has(id))
                            ? 'Deselect All' : 'Select All (Sent + Unsent)';
                        })()}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="divide-y divide-gray-100">
                  {children.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-8">No enrolled children found.</p>
                  )}
                  {children
                    .filter(c => packetCategoryFilter === 'All' ||
                      (c.category || '').toLowerCase() === packetCategoryFilter.toLowerCase())
                    .map(child => {
                    const packetStatus = packetStatuses[child.id];
                    const isSending = sendingPacket === child.id;
                    const alreadySent = !!packetStatus;
                    const isChecked = packetSelectedChildren.has(child.id);
                    // Director-facing "on file" count — sourced from actual Records Management
                    // documents (form_documents), NOT uploadedDocTypes. uploadedDocTypes itself
                    // is untouched and still drives the resend/parent-portal reopen workflow.
                    const applicableKeys = getApplicableStandardForms(child).map(f => f.key);
                    const onFileDocTypes = (packetStatus?.onFileDocTypes || []).filter(k => applicableKeys.includes(k));
                    const uploadedCount = onFileDocTypes.length;
                    const isExpanded = expandedPacketChild === child.id;
                    // Per-child denominator — same applicable-key list as the numerator, so
                    // custom or inapplicable forms can never create a mismatch.
                    const effectiveTemplateCount = applicableKeys.length;
                    // "Resent" is a separate status marker, shown alongside the on-file count —
                    // never substituted for it. Uses a >5s gap (not exact inequality) between
                    // sentAt/lastSentAt so legacy packets created before sentAt/lastSentAt were
                    // unified (microseconds apart) are never mislabeled as resent.
                    const resentLabel = (() => {
                      if (!packetStatus?.lastSentAt || !packetStatus?.sentAt) return '';
                      const diffMs = new Date(packetStatus.lastSentAt) - new Date(packetStatus.sentAt);
                      return diffMs > 5000 ? ` · Resent ${new Date(packetStatus.lastSentAt).toLocaleDateString()}` : '';
                    })();

                    let statusBadge;
                    if (!alreadySent) {
                      statusBadge = <span className="text-xs text-gray-400">Not Sent</span>;
                    } else if (uploadedCount === 0) {
                      statusBadge = packetStatus?.hadUploads ? (
                        <div className="flex flex-col items-end gap-0.5">
                          <span className="text-xs bg-yellow-50 text-yellow-700 font-medium px-2 py-0.5 rounded-full border border-yellow-200" data-testid={`status-badge-${child.id}`}>
                            {uploadedCount} of {effectiveTemplateCount} on file{resentLabel}
                          </span>
                        </div>
                      ) : (
                        <button
                          onClick={() => setExpandedPacketChild(isExpanded ? null : child.id)}
                          className="text-xs bg-yellow-50 text-yellow-700 font-medium px-2 py-0.5 rounded-full border border-yellow-200 hover:bg-yellow-100 flex items-center gap-1"
                          data-testid={`status-badge-${child.id}`}>
                          0 of {effectiveTemplateCount} on file <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                      );
                    } else if (uploadedCount >= effectiveTemplateCount) {
                      statusBadge = (
                        <button
                          onClick={() => setExpandedPacketChild(isExpanded ? null : child.id)}
                          className="text-xs bg-green-50 text-green-700 font-medium px-2 py-0.5 rounded-full border border-green-300 hover:bg-green-100 flex items-center gap-1"
                          data-testid={`status-badge-${child.id}`}>
                          All Forms Received{resentLabel} <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                      );
                    } else {
                      statusBadge = (
                        <button
                          onClick={() => setExpandedPacketChild(isExpanded ? null : child.id)}
                          className="text-xs bg-blue-50 text-blue-600 font-medium px-2 py-0.5 rounded-full border border-blue-200 hover:bg-blue-100 flex items-center gap-1"
                          data-testid={`status-badge-${child.id}`}>
                          {uploadedCount} of {effectiveTemplateCount} on file{resentLabel} <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                      );
                    }

                    return (
                      <div key={child.id} className={`${isChecked ? 'bg-teal-50' : ''}`}>
                        <div className="flex items-center justify-between px-5 py-3 hover:bg-gray-50">
                          <div className="flex items-center gap-3">
                            <input type="checkbox" checked={isChecked}
                              onChange={() => togglePacketChildSelection(child.id)}
                              className="w-4 h-4 accent-teal-600 cursor-pointer"
                              data-testid={`packet-checkbox-${child.id}`} />
                            <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-teal-600" />
                            </div>
                            <span className="text-sm font-medium text-gray-800">{child.name}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            {packetStatus?.pendingPickupSync && (
                              <button
                                onClick={() => setExpandedPacketChild(isExpanded ? null : child.id)}
                                className="text-xs bg-amber-50 text-amber-700 font-medium px-2 py-0.5 rounded-full border border-amber-300 hover:bg-amber-100 flex items-center gap-1 animate-pulse"
                                data-testid={`review-pending-badge-${child.id}`}>
                                Review Pending
                              </button>
                            )}
                            {sixMonthNotifications.some(r => r.childId === child.id && r.status === 'sent') && (
                              <span
                                className="text-xs bg-amber-50 text-amber-700 font-medium px-2 py-0.5 rounded-full border border-amber-300"
                                data-testid={`six-month-badge-${child.id}`}>
                                Emergency Contact — 6 Month Review Sent
                              </span>
                            )}
                            {statusBadge}
                            {alreadySent && (
                              <Button size="sm" variant="outline"
                                className="h-8 text-xs border-gray-300 text-gray-600 hover:bg-gray-50 flex items-center gap-1"
                                onClick={() => printEnrollmentSummary(child)}
                                data-testid={`print-enrollment-summary-${child.id}`}
                                title="Print compliance summary for this child">
                                <Printer className="w-3 h-3" />
                                Print
                              </Button>
                            )}
                            <Button size="sm"
                              className={`h-8 text-xs ${alreadySent ? 'bg-gray-100 hover:bg-gray-200 text-gray-700' : 'bg-teal-600 hover:bg-teal-700 text-white'}`}
                              onClick={() => setResendConfirmChild(child)}
                              disabled={isSending || !!bulkSendProgress}
                              data-testid={`send-enrollment-packet-${child.id}`}>
                              {isSending ? (
                                <div className="flex items-center gap-1.5">
                                  <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                  Sending...
                                </div>
                              ) : alreadySent ? 'Resend Packet' : 'Send Packet'}
                            </Button>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="px-5 pb-3 bg-gray-50 border-t border-gray-100">
                            {/* Stage 3 — Pending Pickup Sync Review Panel */}
                            {packetStatus?.pendingPickupSync && (() => {
                              const pending = packetStatus.pendingPickupSync;
                              const localContacts = reviewContacts[child.id] || pending.contacts || [];
                              return (
                                <div className="mt-3 mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                                  <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-0.5">
                                    Contacts Pending Review — {(pending.extractedFrom || '').replace(/_/g, ' ')}
                                  </p>
                                  <p className="text-xs text-amber-600 mb-3">
                                    Extracted {pending.extractedAt ? new Date(pending.extractedAt).toLocaleDateString() : ''} — edit if needed, then approve to sync to child record
                                  </p>
                                  <div className="space-y-2 mb-3">
                                    {localContacts.map((contact, idx) => (
                                      <div key={idx} className="grid grid-cols-3 gap-2 items-end">
                                        <div>
                                          <p className="text-xs text-gray-400 mb-0.5">{contact.relationship}</p>
                                          <input
                                            className="w-full text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:border-teal-400 bg-white"
                                            value={contact.name}
                                            placeholder="Name"
                                            data-testid={`review-contact-name-${child.id}-${idx}`}
                                            onChange={e => {
                                              const updated = localContacts.map((c, i) => i === idx ? { ...c, name: e.target.value } : c);
                                              setReviewContacts(prev => ({ ...prev, [child.id]: updated }));
                                            }}
                                          />
                                        </div>
                                        <div>
                                          <p className="text-xs text-gray-400 mb-0.5">Phone</p>
                                          <input
                                            className="w-full text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:border-teal-400 bg-white"
                                            value={contact.phone}
                                            placeholder="Phone"
                                            data-testid={`review-contact-phone-${child.id}-${idx}`}
                                            onChange={e => {
                                              const updated = localContacts.map((c, i) => i === idx ? { ...c, phone: e.target.value } : c);
                                              setReviewContacts(prev => ({ ...prev, [child.id]: updated }));
                                            }}
                                          />
                                        </div>
                                        <div>
                                          <p className="text-xs text-gray-400 mb-0.5">Email (optional)</p>
                                          <input
                                            className="w-full text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:border-teal-400 bg-white"
                                            value={contact.email}
                                            placeholder="Email"
                                            data-testid={`review-contact-email-${child.id}-${idx}`}
                                            onChange={e => {
                                              const updated = localContacts.map((c, i) => i === idx ? { ...c, email: e.target.value } : c);
                                              setReviewContacts(prev => ({ ...prev, [child.id]: updated }));
                                            }}
                                          />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => handleApprovePickupSync(child.id)}
                                      className="text-xs bg-teal-600 text-white px-3 py-1.5 rounded hover:bg-teal-700 font-medium"
                                      data-testid={`approve-pickup-sync-${child.id}`}>
                                      Approve &amp; Save to Record
                                    </button>
                                    <button
                                      onClick={() => handleDismissPickupSync(child.id)}
                                      className="text-xs bg-white text-gray-600 px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50"
                                      data-testid={`dismiss-pickup-sync-${child.id}`}>
                                      Dismiss
                                    </button>
                                  </div>
                                </div>
                              );
                            })()}
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-2 pb-1.5">Files Received</p>
                            {alreadySent && uploadedCount >= effectiveTemplateCount && (
                              <p className="text-xs text-green-700 pb-1.5" data-testid={`all-files-received-note-${child.id}`}>
                                All requested files have been received. Review Child Records for any expiring or expired requirements.
                              </p>
                            )}
                            <div className="grid grid-cols-1 gap-0.5">
                              {getApplicableStandardForms(child).map(({ key, label }) => {
                                const received = onFileDocTypes.includes(key);
                                const uploadKey = `${child.id}_${key}`;
                                const isUploading = staffUploading[uploadKey];
                                return (
                                  <div key={key} className="flex items-center gap-2 py-0.5">
                                    {received
                                      ? <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                                      : <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 flex-shrink-0" />}
                                    <span className={`text-xs ${received ? 'text-green-700 font-medium' : 'text-gray-500'}`}>{label}</span>
                                    {!received && (
                                      <label
                                        className="ml-auto cursor-pointer text-teal-600 hover:text-teal-800 flex-shrink-0"
                                        title="Upload on behalf of parent"
                                        data-testid={`staff-upload-${child.id}-${key}`}>
                                        {isUploading
                                          ? <span className="text-xs text-teal-500 animate-pulse">Uploading...</span>
                                          : <Upload className="w-3.5 h-3.5" />}
                                        <input
                                          type="file"
                                          accept=".pdf"
                                          className="hidden"
                                          onChange={(e) => handleStaffUpload(child.id, key, e)}
                                          disabled={isUploading}
                                        />
                                      </label>
                                    )}
                                  </div>
                                );
                              })}
                              {enrollmentTemplates.filter(t => t.isCustom).length > 0 && (
                                <>
                                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide pt-2 pb-0.5">Additional Forms</p>
                                  {enrollmentTemplates.filter(t => t.isCustom).map(tmpl => {
                                    const customStatus = packetStatus?.customFormStatus?.[tmpl.customId];
                                    const isDone = !!customStatus;
                                    return (
                                      <div key={tmpl.customId} className="flex items-center gap-2 py-0.5">
                                        {isDone
                                          ? <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                                          : <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 flex-shrink-0" />}
                                        <span className={`text-xs ${isDone ? 'text-green-700 font-medium' : 'text-gray-500'}`}>
                                          {tmpl.customLabel}
                                          {!tmpl.returnRequired && <span className="text-gray-400 ml-1">(Acknowledge Only)</span>}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Bulk send bar */}
                {packetSelectedChildren.size > 0 && (
                  <div className="border-t border-teal-200 bg-teal-50 px-5 py-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-teal-700 font-medium">
                        {bulkSendProgress
                          ? `Sending ${bulkSendProgress.current} of ${bulkSendProgress.total}...`
                          : `${packetSelectedChildren.size} ${packetSelectedChildren.size === 1 ? 'child' : 'children'} selected`}
                      </span>
                      <Button size="sm"
                        className="bg-teal-600 hover:bg-teal-700 text-white text-xs h-8"
                        onClick={() => setBulkConfirmOpen(true)}
                        disabled={!!bulkSendProgress || bulkConfirmOpen}
                        data-testid="bulk-send-packets-btn">
                        {bulkSendProgress ? (
                          <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Sending...
                          </div>
                        ) : `Send to Selected (${packetSelectedChildren.size})`}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </TabsContent>
          )} {/* end enrollmentPermitted */}

          {/* PDF Field Name Guide modal — Stage 1 */}
          <PdfFieldGuideModal
            open={pdfGuideOpen}
            onClose={() => setPdfGuideOpen(false)}
            initialTab={pdfGuideInitialTab}
          />

          {/* Stage 2 — Dry-run field validation result */}
          <AlertDialog open={!!dryRunResult} onOpenChange={o => { if (!o) setDryRunResult(null); }}>
            <AlertDialogContent className="max-w-lg" data-testid="dry-run-result-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  {dryRunResult && !dryRunResult.hasAcroForm && <>📄 Template uploaded — but no interactive fields</>}
                  {dryRunResult && dryRunResult.hasAcroForm && dryRunResult.foundMappable === dryRunResult.mappableFields && <>✅ Fully ready for auto-fill</>}
                  {dryRunResult && dryRunResult.hasAcroForm && dryRunResult.foundMappable > 0 && dryRunResult.foundMappable < dryRunResult.mappableFields && <>ℹ️ Ready — {dryRunResult.foundMappable} of {dryRunResult.mappableFields} auto-fill fields found</>}
                  {dryRunResult && dryRunResult.hasAcroForm && dryRunResult.foundMappable === 0 && <>⚠️ No auto-fill fields found yet</>}
                </AlertDialogTitle>
                <AlertDialogDescription className="text-sm text-gray-700">
                  {dryRunResult?.formLabel}
                </AlertDialogDescription>
              </AlertDialogHeader>
              {dryRunResult && !dryRunResult.hasAcroForm && (
                <div className="text-sm text-gray-700 bg-amber-50 border border-amber-200 rounded p-3">
                  {dryRunResult.message || 'This PDF is a flat / scanned document with no AcroForm fields. Parents can still fill and submit it, but no data will auto-fill on Edit Child. Add named fields in Adobe Acrobat Pro to enable auto-fill.'}
                </div>
              )}
              {dryRunResult?.hasAcroForm && (
                <div className="space-y-3">
                  <div className="rounded p-3 border" style={{
                    background: dryRunResult.foundMappable === dryRunResult.mappableFields ? '#ecfdf5' : '#eff6ff',
                    borderColor: dryRunResult.foundMappable === dryRunResult.mappableFields ? '#a7f3d0' : '#bfdbfe',
                  }}>
                    <div className="font-semibold text-sm" data-testid="dry-run-summary">
                      Found {dryRunResult.foundMappable} of {dryRunResult.mappableFields} auto-fill field{dryRunResult.mappableFields === 1 ? '' : 's'} for this form
                    </div>
                    <div className="text-xs text-gray-700 mt-1">
                      These are the only fields this form syncs to a child record.
                    </div>
                  </div>

                  {dryRunResult.missingMappable?.length > 0 && (
                    <div className="text-sm">
                      <div className="font-semibold text-gray-800 mb-1.5">To capture more, name these fields per the guide:</div>
                      <ul className="max-h-32 overflow-y-auto space-y-1 border rounded p-2 bg-blue-50 font-mono text-xs">
                        {dryRunResult.missingMappable.map(n => (
                          <li key={n} className="text-blue-800">• {n}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {dryRunResult.unmatched?.length > 0 && (
                    <div className="text-sm">
                      <div className="font-semibold text-gray-600 mb-1.5">Other fields in your PDF ({dryRunResult.unmatched.length}) — these are fine</div>
                      <div className="text-xs text-gray-500 mb-1.5">Parents fill these; they just don't sync to a child record. No action needed.</div>
                      <ul className="max-h-28 overflow-y-auto space-y-1 border rounded p-2 bg-gray-50 font-mono text-xs">
                        {dryRunResult.unmatched.slice(0, 30).map(n => (
                          <li key={n} className="text-gray-500">• {n}</li>
                        ))}
                        {dryRunResult.unmatched.length > 30 && (
                          <li className="text-gray-400 italic">…and {dryRunResult.unmatched.length - 30} more</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              <AlertDialogFooter>
                {dryRunResult?.hasAcroForm && dryRunResult.missingMappable?.length > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setPdfGuideInitialTab(dryRunResult.documentType || 'start');
                      setPdfGuideOpen(true);
                    }}
                    data-testid="dry-run-open-guide-btn"
                  >
                    <FileText className="w-4 h-4 mr-1.5" /> Open Field Name Guide
                  </Button>
                )}
                <AlertDialogAction onClick={() => setDryRunResult(null)} data-testid="dry-run-close-btn">
                  Got it
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Bulk Send/Resend Confirmation Modal — independent of resendConfirmChild */}
          <AlertDialog open={bulkConfirmOpen} onOpenChange={open => { setBulkConfirmOpen(open); if (!open) { setBulkSendViaEmail(true); setBulkSendViaSMS(true); } }}>
            <AlertDialogContent data-testid="bulk-send-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Send Enrollment Packets to {packetSelectedChildren.size} {packetSelectedChildren.size === 1 ? 'Child' : 'Children'}?
                </AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-3 text-sm text-gray-600">
                    {(() => {
                      const toSendList = children.filter(c => packetSelectedChildren.has(c.id));
                      const firstTimeChildren = toSendList.filter(c => !packetStatuses[c.id]);
                      const resendChildren = toSendList.filter(c => !!packetStatuses[c.id]);
                      const allFormsSelected = selectedPacketForms.size >= ENROLL_FORMS_LIST.length;
                      return (
                        <>
                          {firstTimeChildren.length > 0 && (
                            <p>
                              <strong>{firstTimeChildren.length} first-time send{firstTimeChildren.length > 1 ? 's' : ''}:</strong>{' '}
                              {firstTimeChildren.map(c => c.name).join(', ')}
                            </p>
                          )}
                          {resendChildren.length > 0 && (
                            <>
                              <p>
                                <strong>{resendChildren.length} resend{resendChildren.length > 1 ? 's' : ''}:</strong>{' '}
                                {resendChildren.map(c => c.name).join(', ')}
                              </p>
                              <p className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-800 text-xs">
                                {allFormsSelected
                                  ? 'This full resend will reopen all applicable forms.'
                                  : 'This resend will reopen only the selected forms.'}
                                {' '}Currently accepted documents remain on file until a replacement is approved.
                              </p>
                            </>
                          )}
                          <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 space-y-2">
                            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Send via:</p>
                            <label className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={bulkSendViaEmail}
                                onChange={e => setBulkSendViaEmail(e.target.checked)}
                                className="w-4 h-4 accent-teal-600"
                                data-testid="bulk-send-via-email-toggle"
                              />
                              <span className="text-sm text-gray-700 font-medium">Email</span>
                            </label>
                            <label className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={bulkSendViaSMS}
                                onChange={e => setBulkSendViaSMS(e.target.checked)}
                                className="w-4 h-4 accent-teal-600"
                                data-testid="bulk-send-via-sms-toggle"
                              />
                              <span className="text-sm text-gray-700 font-medium">SMS</span>
                            </label>
                            {!bulkSendViaEmail && !bulkSendViaSMS && (
                              <p className="text-xs text-red-600 font-medium" data-testid="bulk-send-no-channel-msg">Select at least one delivery method.</p>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">
                            Packets will be delivered via the selected method(s) to whichever contact info is on file for each parent.
                          </p>
                          <div className="space-y-1">
                            <Label htmlFor="bulk-packet-note" className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                              Note to parent (optional)
                            </Label>
                            <Textarea
                              id="bulk-packet-note"
                              value={bulkPacketNote}
                              onChange={e => setBulkPacketNote(e.target.value)}
                              placeholder="e.g. Please also bring proof of address."
                              className="text-sm"
                              rows={2}
                              data-testid="bulk-modal-packet-note-textarea"
                            />
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="bulk-send-cancel-btn">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="bulk-send-confirm-btn"
                  disabled={!bulkSendViaEmail && !bulkSendViaSMS}
                  onClick={() => { setBulkConfirmOpen(false); sendToSelected(); }}>
                  Yes, Send to {packetSelectedChildren.size} {packetSelectedChildren.size === 1 ? 'Child' : 'Children'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Resend Packet Confirmation Modal */}
          <AlertDialog open={!!resendConfirmChild} onOpenChange={open => { if (!open) { setResendConfirmChild(null); setSendViaEmail(true); setsendViaSMS(true); setPacketNote(''); } }}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{resendConfirmChild && packetStatuses[resendConfirmChild.id] ? 'Resend Enrollment Packet?' : 'Send Enrollment Packet?'}</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-3 text-sm text-gray-600">
                    <p>
                      You are about to {resendConfirmChild && packetStatuses[resendConfirmChild.id] ? 'resend' : 'send'} the enrollment packet to <strong>{resendConfirmChild?.name?.includes(',') ? resendConfirmChild.name.split(',').reverse().map(s => s.trim()).join(' ') : resendConfirmChild?.name}</strong>'s parent.
                    </p>
                    {(() => {
                      if (!resendConfirmChild) return null;
                      const applicableKeys = getApplicableStandardForms(resendConfirmChild).map(f => f.key);
                      const onFileCount = (packetStatuses[resendConfirmChild.id]?.onFileDocTypes || [])
                        .filter(k => applicableKeys.includes(k)).length;
                      if (onFileCount === 0) return null;
                      return (
                        <p className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-800 text-xs">
                          This child currently has <strong>{onFileCount} of {applicableKeys.length}</strong> required forms on file.
                          Current accepted forms will remain on file until you approve their replacements.
                        </p>
                      );
                    })()}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 space-y-2">
                      <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Send via:</p>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={sendViaEmail}
                          onChange={e => setSendViaEmail(e.target.checked)}
                          className="w-4 h-4 accent-teal-600"
                          data-testid="send-via-email-toggle"
                        />
                        <span className="text-sm text-gray-700 font-medium">Email</span>
                      </label>
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={sendViaSMS}
                          onChange={e => setsendViaSMS(e.target.checked)}
                          className="w-4 h-4 accent-teal-600"
                          data-testid="send-via-sms-toggle"
                        />
                        <span className="text-sm text-gray-700 font-medium">SMS</span>
                      </label>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="packet-note" className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                        Note to parent (optional)
                      </Label>
                      <Textarea
                        id="packet-note"
                        value={packetNote}
                        onChange={e => setPacketNote(e.target.value)}
                        placeholder="e.g. Please also bring proof of address."
                        className="text-sm"
                        rows={2}
                        data-testid="packet-note-textarea"
                      />
                    </div>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => { setResendConfirmChild(null); setSendViaEmail(true); setsendViaSMS(true); setPacketNote(''); }}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-teal-600 hover:bg-teal-700 disabled:opacity-50"
                  disabled={!sendViaEmail && !sendViaSMS}
                  onClick={() => { sendEnrollmentPacket(resendConfirmChild, sendViaEmail, sendViaSMS, packetNote); setResendConfirmChild(null); setSendViaEmail(true); setsendViaSMS(true); setPacketNote(''); }}
                  data-testid="confirm-resend-btn">
                  {resendConfirmChild && packetStatuses[resendConfirmChild.id] ? 'Yes, Resend Packet' : 'Yes, Send Packet'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Tracker Print — Inspection Date Picker */}
          <Dialog open={showTrackerDatePicker} onOpenChange={open => { if (!open) setShowTrackerDatePicker(false); }}>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Printer className="w-4 h-4 text-teal-600" />
                  Set Inspection Date
                </DialogTitle>
              </DialogHeader>
              <div className="py-2 space-y-3">
                <p className="text-sm text-gray-600">
                  This date will appear on the printed filing sheet. Set it to match the actual inspection or filing date.
                </p>
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-1 block">As-of Date</Label>
                  <Input
                    type="date"
                    value={trackerPrintAsOfDate}
                    onChange={e => setTrackerPrintAsOfDate(e.target.value)}
                    className="w-full"
                    data-testid="tracker-print-as-of-date-input"
                  />
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowTrackerDatePicker(false)}>Cancel</Button>
                <Button
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                  onClick={() => {
                    setShowTrackerDatePicker(false);
                    printTrackerReviews(pendingTrackerReviews, trackerPrintAsOfDate);
                  }}
                  data-testid="confirm-tracker-print-btn"
                >
                  <Printer className="w-4 h-4 mr-1" />
                  Print Report
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Compliance Print — Inspection Date Picker */}
          <Dialog open={showPrintDatePicker} onOpenChange={open => { if (!open) setShowPrintDatePicker(false); }}>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Printer className="w-4 h-4 text-teal-600" />
                  Set Inspection Date
                </DialogTitle>
              </DialogHeader>
              <div className="py-2 space-y-3">
                <p className="text-sm text-gray-600">
                  This date will appear on the printed compliance report. Set it to match the actual inspection date.
                </p>
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-1 block">As-of Date</Label>
                  <Input
                    type="date"
                    value={printAsOfDate}
                    onChange={e => setPrintAsOfDate(e.target.value)}
                    className="w-full"
                    data-testid="print-as-of-date-input"
                  />
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowPrintDatePicker(false)}>Cancel</Button>
                <Button
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                  onClick={() => {
                    setShowPrintDatePicker(false);
                    firePrint(pendingPrintTarget, printAsOfDate);
                  }}
                  data-testid="confirm-print-report-btn"
                >
                  <Printer className="w-4 h-4 mr-1" />
                  Print Report
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Clear All Notifications — confirmation dialog */}
          <AlertDialog open={showClearAllConfirm} onOpenChange={setShowClearAllConfirm}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear All Notifications?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will remove all {notifChildGroups.length} new-document notifications. The uploaded files themselves will not be deleted — only the alerts will be dismissed. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setShowClearAllConfirm(false)}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => { setShowClearAllConfirm(false); clearAllNotifications(); }}
                  data-testid="confirm-clear-all-notifications"
                >
                  Yes, Clear All
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <TabsContent value="reminders">
            <RecordRemindersSettings />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default ChildrenRecordsTab;
